import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, ListSubheader, MenuItem, Paper,
  Snackbar, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ConstructionIcon from '@mui/icons-material/Construction';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { useNavigate } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { addMonths, format } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { useQuotationStore } from '../../store/quotationStore';
import {
  PHP, computeTotals, componentLineTotal,
  componentSellingUnit, lineGeneralTotal, manpowerCost, manpowerTotalCost, manpowerDailyRate,
  formatDiscountPct, discountPctFromAmount, discountPctFromTargetNet,
} from '../../utils/calcsheet/calc';
import { ISSUER_NAMES, DEFAULT_SCOPE_OF_WORK, defaultBasisOfProposal, defaultDeliveryText, defaultTaxStatusTerm, DEFAULT_WARRANTY_EXCLUSION } from '../../utils/calcsheet/defaultTerms';
import { quotationRefNo } from '../../utils/calcsheet/codes';
import { sanitizeNumericText, parseLenientFloat, parseLenientInt } from '../../utils/calcsheet/numberInput';
import { FloatingTotalsWidget } from './FloatingTotalsWidget';
import type {
  ComponentLine, GeneralReqLine, HistoricalPriceSource, ManpowerEntry, Quotation, QuotationVersion, SalesContact, ServiceLine,
} from '../../types/Quotation';
import { EditableTable } from './EditableTable';
import type { Column, ContextMenuItem } from './EditableTable';
import DuplicateQuotationDialog from './DuplicateQuotationDialog';
import CopyInclusionsDialog, { type CopiedInclusions } from './CopyInclusionsDialog';
import ScopeLibraryDialog from './ScopeLibraryDialog';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import { exportQuotationPdf, findSalesContact } from '../../utils/calcsheet/pdfExport';
import { compactLayoutAvailability, type QuotationPdfLayout } from '../../utils/calcsheet/quotationPdfLayout';
import { exportQuotationXlsx } from '../../utils/calcsheet/xlsxExport';
import { findInternalLookingText } from '../../utils/calcsheet/internalText';
import InternalNoteDialog, { InternalNoteButton } from './InternalNoteDialog';
import InternalTextWarning from './InternalTextWarning';
import { useOneDriveAuth } from '../../contexts/OneDriveAuthContext';
import { useAuth } from '../../contexts/AuthContext';
import { isCorporateOneDriveConfigured } from '../../config/onedriveConfig';
import { resolveCorporateDriveId, uploadFileToFolderById } from '../../services/onedriveFolderService';
import ProductPickerDialog from '../pricelists/ProductPickerDialog';
import InstallationWorkDialog from './InstallationWorkDialog';
import { INSTALLATION_HEADER, withInstallationRows, withSectionRows } from '../../utils/calcsheet/installationMaterials';
import SiemensPlcDialog, { type PlcSubmitSection } from './SiemensPlcDialog';
import DesigoBmsDialog from './DesigoBmsDialog';
import ControlPanelDialog from './ControlPanelDialog';
import MemoryIcon from '@mui/icons-material/Memory';
import type { PricelistItem } from '../../types/Pricelist';
import type { ProductHistoryAddSelection } from '../../types/ProductHistory';
import ComponentTimingDialog, {
  ComponentTimingAction,
} from './ComponentTimingDialog';
import { hasInvalidPurchaseTiming } from './purchaseTiming';
import { subheaderBefore } from '../../utils/calcsheet/subheaders';

const id = () => nanoid(6);

const PAYMENT_TERM_OPTIONS = [
  '30% Downpayment, 70% Progress Billing',
  '30% Downpayment, 60% Progress Billing, 10% Retention',
  '20% Downpayment, 80% Progress Billing',
  '20% Downpayment, 70% Progress Billing, 10% Retention',
  '50% Downpayment, 50% Progress Billing',
  '50% Downpayment, 50% Upon Completion',
  '100% Upon Completion',
  // "Pay-when-paid" — IOCT/ACTI invoice the customer in lockstep with the
  // end-user paying them. Common in subcontracted automation work where the
  // customer is themselves billing a downstream owner.
  'Back-to-back with end-user payment terms',
];
const CUSTOM_PAYMENT = '__custom__';
const todayDateOnly = () => format(new Date(), 'yyyy-MM-dd');

export const resolveExpectedPurchaseDate = (
  quotationDate: string,
  quotationExpectedPurchaseDate?: string,
) => quotationExpectedPurchaseDate
  || format(addMonths(new Date(`${quotationDate}T00:00:00`), 3), 'yyyy-MM-dd');

export const createHistoricalComponentLine = (
  selection: ProductHistoryAddSelection,
  defaultContingencyPct: number,
  code: string,
  lineId: string,
  selectedAt = new Date().toISOString(),
): ComponentLine => {
  const item = selection.observation;
  if (
    !item.quotationDate
    || item.normalizedUnitCost == null
    || !Number.isFinite(item.normalizedUnitCost)
    || item.normalizedUnitCost <= 0
  ) {
    throw new Error('Historical product requires a usable quotation date and normalized cost');
  }
  const suggested = selection.applySuggestion
    && selection.suggestion?.status === 'ready'
    ? selection.suggestion.suggestedContingencyPct
    : null;
  return {
    id: lineId,
    code,
    description: item.description,
    brand: item.brand || '',
    partNo: item.partNo || '',
    qty: 1,
    uom: item.uom || 'pc',
    unitCost: item.sourceUnitCost,
    forex: item.sourceForex || 1,
    discountPct: item.sourceDiscountPct || 0,
    ...(selection.expectedPurchaseDateOverride
      ? { expectedPurchaseDate: selection.expectedPurchaseDateOverride }
      : {}),
    contingencyPct: suggested ?? defaultContingencyPct,
    contingencyPctOverridden: suggested != null,
    historicalPriceSource: {
      observationId: item.observationId,
      quotationId: item.quotationId,
      projectId: item.projectId,
      quotationReference: item.quotationReference,
      quotationDate: item.quotationDate,
      normalizedUnitCost: item.normalizedUnitCost,
      ...(item.quotedSellingUnit != null
        ? { quotedSellingUnit: item.quotedSellingUnit }
        : {}),
      selectedAt,
      ...(suggested != null ? {
        suggestedContingencyPct: suggested,
        ...(selection.suggestion?.method
          ? { suggestionMethod: selection.suggestion.method }
          : {}),
        ...(selection.suggestion?.confidence
          ? { suggestionConfidence: selection.suggestion.confidence }
          : {}),
      } : {}),
    },
  };
};

export function HistoricalSourceChip({ source }: { source: HistoricalPriceSource }) {
  return (
    <Tooltip
      title={[
        source.quotationReference,
        source.quotationDate,
        `Base cost ${PHP(source.normalizedUnitCost)}`,
      ].join(' · ')}
    >
      <Chip
        label="Quote history"
        size="small"
        variant="outlined"
        color="info"
        sx={{ flexShrink: 0 }}
      />
    </Tooltip>
  );
}

const normalizeName = (value: string | undefined | null) => (value || '').trim().toLowerCase();
const firstLastKey = (value: string | undefined | null) => {
  const parts = normalizeName(value).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

// Number input that doesn't force a leading 0 — empty when value is 0, select-all on focus.
// Keeps its own text buffer instead of deriving the display string from `value` on every
// keystroke — otherwise typing a trailing "0" after a decimal point (e.g. "11.50") gets
// immediately stripped back to "11.5" because String(11.5) === '11.5'.
function NumField({
  label, value, onChange, integer = false, sx, helperText, disabled,
}: { label: string; value: number; onChange: (v: number) => void; integer?: boolean; sx?: any; helperText?: string; disabled?: boolean }) {
  const [text, setText] = useState(() => (value === 0 ? '' : String(value)));
  const lastEmitted = useRef(value);
  const focused = useRef(false);

  useEffect(() => {
    // Only resync from outside (e.g. another control writing the same field, or a reset) —
    // not from the onChange we just fired ourselves, which would clobber in-progress typing.
    // Some callers pass a `value` derived by round-tripping through other math (e.g. a peso
    // discount recomputed from a stored percentage) rather than echoing what was typed —
    // that round trip rarely lands on the exact same float, so skip resyncing while the
    // field is focused or we'd stomp on the user's keystrokes mid-type.
    if (focused.current) return;
    if (value !== lastEmitted.current) {
      setText(value === 0 ? '' : String(value));
      lastEmitted.current = value;
    }
  }, [value]);

  return (
    <TextField
      label={label}
      size="small"
      type="text"
      inputMode="decimal"
      value={text}
      placeholder="0"
      onChange={(e) => {
        // type="number" can't parse thousands separators and browsers are
        // inconsistent (sometimes outright buggy) about pasted comma-formatted
        // figures like "503,170.08" copied from Excel/a PDF — so this is a
        // plain text field and we sanitize+parse pasted/typed input ourselves.
        const raw = sanitizeNumericText(e.target.value);
        setText(raw);
        const parsed = integer ? parseLenientInt(raw) : parseLenientFloat(raw);
        lastEmitted.current = parsed;
        onChange(parsed);
      }}
      onFocus={(e) => { focused.current = true; e.target.select(); }}
      onBlur={() => {
        focused.current = false;
        if (value !== lastEmitted.current) {
          setText(value === 0 ? '' : String(value));
          lastEmitted.current = value;
        }
      }}
      disabled={disabled}
      sx={sx}
      helperText={helperText}
    />
  );
}

interface BreakdownProps {
  label: string;
  color: 'primary' | 'secondary';
  cost: number;
  contingency: number | null;
  contingencyPct: number | null;
  markup: number;
  markupPct: number;
  subtotal: number;
}

function BreakdownCard({ label, color, cost, contingency, contingencyPct, markup, markupPct, subtotal }: BreakdownProps) {
  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" sx={{ color: `${color}.main`, fontWeight: 600 }}>{label}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 0.5, mt: 1, fontSize: '0.75rem' }}>
        <Typography variant="caption" color="text.secondary">Cost</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(cost)}</Typography>
        {contingency !== null && contingencyPct !== null && (
          <>
            <Typography variant="caption" color="text.secondary">+ Contingency ({contingencyPct}%)</Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(contingency)}</Typography>
          </>
        )}
        <Typography variant="caption" color="text.secondary">+ Markup ({markupPct}%)</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(markup)}</Typography>
        <Divider sx={{ gridColumn: 'span 2', my: 0.25 }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Subtotal</Typography>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>{PHP(subtotal)}</Typography>
      </Box>
    </Box>
  );
}

// Shift+click range-select on a row-checkbox column — mirrors OS file-manager
// selection (Explorer/Finder), but symmetric for checkboxes: a plain click
// toggles just that row and remembers both the row (the "anchor") and which
// way it just went (checked or unchecked); a Shift+click replays that SAME
// action across every row between the anchor and the clicked row — so
// Shift+click both multi-selects (anchor was just checked) and
// multi-deselects (anchor was just unchecked). Rows outside the range are
// never touched.
//
// The anchor is remembered by row id, not array position — resolved back to
// a live index at Shift+click time — so it stays correct even if a row is
// added, deleted, or drag-reordered in between the two clicks (an index
// captured at click time would otherwise silently point at the wrong row
// once the array shifts under it). `rows`/`idx` are the same array/position
// EditableTable's column `render(row, idx)` already gets, so the range
// always matches what's on screen.
function rangeSelectHandler<T extends { id: string }>(
  rows: T[],
  selectedIds: Set<string>,
  anchorRef: React.MutableRefObject<{ id: string; checked: boolean } | null>,
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>,
) {
  return (idx: number) => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const anchorIdx = anchorRef.current ? rows.findIndex((r) => r.id === anchorRef.current!.id) : -1;
    if (e.shiftKey && anchorIdx >= 0) {
      const { checked } = anchorRef.current!;
      const from = Math.min(anchorIdx, idx);
      const to = Math.max(anchorIdx, idx);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (let i = from; i <= to; i++) {
          if (checked) next.add(rows[i].id); else next.delete(rows[i].id);
        }
        return next;
      });
      // Anchor stays put — repeated Shift+click from the same starting point
      // keeps growing/shrinking the same range, matching Explorer/Finder.
    } else {
      const rowId = rows[idx].id;
      const willCheck = !selectedIds.has(rowId);
      anchorRef.current = { id: rowId, checked: willCheck };
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (willCheck) next.add(rowId); else next.delete(rowId);
        return next;
      });
    }
  };
}

// Header "select all / none" checkbox for a row-checkbox column — one click
// selects every row in the (currently displayed) section, or clears it if
// everything's already selected; shows the usual indeterminate dash while
// only some rows are checked. Clears any pending Shift+click anchor so a
// later range-select starts fresh relative to the bulk action, not a row
// that may no longer reflect the same context.
function selectAllHeaderCheckbox<T extends { id: string }>(
  rows: T[],
  selectedIds: Set<string>,
  anchorRef: React.MutableRefObject<{ id: string; checked: boolean } | null>,
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>,
) {
  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const someSelected = !allSelected && rows.some((r) => selectedIds.has(r.id));
  return (
    <Checkbox
      size="small"
      sx={{ p: 0 }}
      checked={allSelected}
      indeterminate={someSelected}
      disabled={rows.length === 0}
      onChange={(e) => {
        anchorRef.current = null;
        setSelectedIds(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set());
      }}
      title={allSelected ? 'Clear selection' : 'Select all rows'}
    />
  );
}

export default function QuotationEditor() {
  const { id: qid = '' } = useParams();
  const navigate = useNavigate();
  // `saved` is the persisted version from the store. `draft` is the in-progress
  // edit state that's only pushed to the server when the user clicks Save.
  // Every keystroke used to fire a PUT; now edits accumulate locally first.
  const saved = useQuotationStore((s) => s.quotations.find((q) => q.id === qid));
  const project = useQuotationStore((s) => saved ? s.projects.find((p) => p.id === saved.projectId) : undefined);
  const clients = useQuotationStore((s) => s.clients);
  const salesContacts = useQuotationStore((s) => s.salesContacts);
  const presets = useQuotationStore((s) => s.laborPresets);
  const update = useQuotationStore((s) => s.updateQuotation);
  const fetchQuotationVersions = useQuotationStore((s) => s.fetchQuotationVersions);
  const deleteQuotationVersion = useQuotationStore((s) => s.deleteQuotationVersion);

  const [draft, setDraft] = useState<Quotation | undefined>(saved);
  const [selectedSvcIds, setSelectedSvcIds] = useState<Set<string>>(new Set());
  const [selectedCompIds, setSelectedCompIds] = useState<Set<string>>(new Set());
  // Last-clicked row (position + resulting checked state) per section, for
  // Shift+click range-select (rangeSelectHandler above).
  const svcSelectAnchor = useRef<{ id: string; checked: boolean } | null>(null);
  const compSelectAnchor = useRef<{ id: string; checked: boolean } | null>(null);
  const [groupDialogOpen, setGroupDialogOpen] = useState<'services' | 'components' | false>(false);
  const [groupLabel, setGroupLabel] = useState('');
  const [subheaderDialogOpen, setSubheaderDialogOpen] = useState<'services' | 'components' | false>(false);
  const [subheaderLabel, setSubheaderLabel] = useState('');
  const [showFloatingTotals, setShowFloatingTotals] = useState(
    () => localStorage.getItem('calcsheet:floatingTotalsVisible') === '1',
  );
  const toggleFloatingTotals = () => setShowFloatingTotals((v) => {
    const next = !v;
    try { localStorage.setItem('calcsheet:floatingTotalsVisible', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });

  // Re-init the draft when the URL points at a different quotation OR when the
  // saved record changes via some path we don't control (e.g. another browser
  // tab saving). Compare by id and updatedAt so we don't clobber the in-flight
  // draft while the user is editing the same quotation.
  useEffect(() => {
    if (!saved) return;
    if (!draft || draft.id !== saved.id) {
      setDraft(saved);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved?.id]);

  const totals = useMemo(() => draft ? computeTotals(draft) : null, [draft]);

  // Margin summary: separates true markup (= profit on a clean run) from the
  // contingency reserve (= buffer for overruns; not earned profit). Total
  // margin excludes contingency — contingency is reported as a separate line
  // so the user can see the cushion without conflating it with earnings.
  //
  // Stack on the selling-price side (subtotal): cost -> +contingency -> +markup.
  // markupOnly per category = subtotal − withContingency.
  // Services bypass cost/contingency entirely when entered as flat amounts
  // (servicesFromManpower=false) — in that case there's no markup either.
  const marginSummary = useMemo(() => {
    if (!draft || !totals) return null;
    const servicesFromMP = !!draft.servicesFromManpower;
    const componentsWithContingency = totals.componentsWithContingency ?? totals.componentsCost;
    // Manual lump-sum service lines have no markup step of their own, but
    // EWT (see Quotation.ewtPct) still applies at display time on top of
    // whatever was typed — so unlike before, that branch is no longer
    // unconditionally 0 once EWT is set.
    const markupOnly =
      (totals.generalReqtsSubtotal - totals.generalReqtsWithContingency) +
      (totals.componentsSubtotal - componentsWithContingency) +
      (servicesFromMP
        ? totals.servicesSubtotal - totals.laborWithContingency
        : totals.servicesSubtotal - totals.laborCost);
    const contingency =
      (totals.generalReqtsWithContingency - totals.generalReqtsCost) +
      (componentsWithContingency - totals.componentsCost) +
      (servicesFromMP ? totals.laborWithContingency - totals.laborCost : 0);
    const subtotal =
      totals.generalReqtsSubtotal +
      totals.componentsSubtotal +
      totals.servicesSubtotal;
    const pct = subtotal > 0 ? (markupOnly / subtotal) * 100 : 0;
    const contingencyPct = subtotal > 0 ? (contingency / subtotal) * 100 : 0;
    return { value: markupOnly, subtotal, pct, contingency, contingencyPct };
  }, [draft, totals]);

  const isDirty = useMemo(() => {
    if (!draft || !saved) return false;
    if (draft.id !== saved.id) return false;
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }, [draft, saved]);

  // Warn the user before closing/reloading the tab if there are unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const [saving, setSaving] = useState(false);

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [installWorkOpen, setInstallWorkOpen] = useState(false);
  const [plcOpen, setPlcOpen] = useState(false);
  const [desigoOpen, setDesigoOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [timingComponentId, setTimingComponentId] = useState<string | null>(null);
  // Internal note (team-only, never printed) being viewed/edited, and the Excel "possible internal text" prompt.
  const [noteTarget, setNoteTarget] = useState<{ section: 'components' | 'services'; id: string } | null>(null);
  const [xlsxWarnOpen, setXlsxWarnOpen] = useState(false);

  // "What did you change?" prompt shown on Save — the note is attached to
  // the version snapshot the server takes of the pre-save state.
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  // Terms & Conditions confirmation checkbox — required on both the Save
  // prompt and before Export PDF, so the team consciously re-checks (esp.
  // Delivery terms) instead of forgetting to update it. Resets to unchecked
  // every time either dialog opens — this is a manual "I looked" gate, not a
  // one-time setting.
  const [saveTermsConfirmed, setSaveTermsConfirmed] = useState(false);
  const [exportConfirmOpen, setExportConfirmOpen] = useState(false);
  const [exportTermsConfirmed, setExportTermsConfirmed] = useState(false);
  const [pendingExportLayout, setPendingExportLayout] = useState<QuotationPdfLayout>('standard');

  // Saved-version history (snapshots captured server-side on every save).
  const [historyOpen, setHistoryOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Merge picked inclusions (from another quotation, or the Scope Library) into
  // the current draft. Lines already carry fresh ids from the source dialog.
  // A section locked by this quotation's scope category (see scopeCategory
  // below) never receives picked lines — its EditableTable renders readOnly,
  // so anything appended there would be stuck: visible, uneditable, and
  // undeletable through the UI. Returns whether anything was actually
  // skipped, so the caller can tell the user.
  const appendInclusions = (picked: CopiedInclusions): boolean => {
    const skipped =
      (generalReqtsLocked && picked.generalReqts.length > 0) ||
      (componentsLocked && picked.components.length > 0) ||
      (servicesLocked && (picked.services.length > 0 || picked.manpower.length > 0));
    setDraft((d) => d ? {
      ...d,
      generalReqts: generalReqtsLocked ? d.generalReqts : [...d.generalReqts, ...picked.generalReqts],
      components: componentsLocked ? d.components : [...d.components, ...picked.components],
      services: servicesLocked ? d.services : [...d.services, ...picked.services],
      manpower: servicesLocked ? d.manpower : [...d.manpower, ...picked.manpower],
      ...(picked.terms ? { termsOverrides: { ...d.termsOverrides, ...picked.terms } } : {}),
    } : d);
    return skipped;
  };

  // Append copied inclusions (from another quotation) into the current draft.
  const handleCopyInclusions = (picked: CopiedInclusions) => {
    const skipped = appendInclusions(picked);
    setCopyOpen(false);
    setToast({
      msg: skipped
        ? 'Inclusions copied — some lines were skipped (locked by this quotation\'s scope). Review and Save.'
        : 'Inclusions copied — review and Save.',
      sev: skipped ? 'warning' : 'success',
    });
  };

  // Insert inclusions chosen from the Scope Library into the current draft.
  const handleInsertFromLibrary = (picked: CopiedInclusions) => {
    const skipped = appendInclusions(picked);
    setLibraryOpen(false);
    setToast({
      msg: skipped
        ? 'Inserted from library — some lines were skipped (locked by this quotation\'s scope). Review and Save.'
        : 'Inserted from library — review and Save.',
      sev: skipped ? 'warning' : 'success',
    });
  };
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [versions, setVersions] = useState<QuotationVersion[]>([]);
  const [deletingVersionId, setDeletingVersionId] = useState<string | null>(null);

  // OneDrive auto-upload on export. Hooks must be declared unconditionally —
  // keep these above the early-return guard below.
  const { isAuthenticated: oneDriveSignedIn, getAccessToken: getOneDriveToken } = useOneDriveAuth();
  const { user: currentUser } = useAuth();
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'warning' | 'info' } | null>(null);
  const effectiveSalesContacts = useMemo<SalesContact[]>(() => {
    const userName = (currentUser?.full_name?.trim() || currentUser?.username || currentUser?.email || '').trim();
    const userPosition = (currentUser?.designation || '').trim();
    if (!userName && !userPosition) return salesContacts;
    const userEmail = (currentUser?.email || '').trim();
    const userNameKey = normalizeName(userName);
    const userFirstLast = firstLastKey(userName);
    const currentUserContact: SalesContact = {
      id: String(currentUser?.id || 'current-user'),
      name: userName,
      position: userPosition,
      email: userEmail,
      phone: (currentUser?.contact_number || '').trim(),
    };
    const merged = salesContacts.map((contact) => {
      const contactNameKey = normalizeName(contact.name);
      const contactFirstLast = firstLastKey(contact.name);
      const isCurrentUser =
        (!!userNameKey && contactNameKey === userNameKey) ||
        (!!userFirstLast && contactFirstLast === userFirstLast) ||
        (!!userEmail && normalizeName(contact.email) === normalizeName(userEmail));
      return isCurrentUser
        ? { ...contact, position: userPosition || contact.position, email: userEmail || contact.email }
        : contact;
    });
    const alreadyListed = merged.some((contact) => {
      const contactNameKey = normalizeName(contact.name);
      const contactFirstLast = firstLastKey(contact.name);
      return (
        (!!userNameKey && contactNameKey === userNameKey) ||
        (!!userFirstLast && contactFirstLast === userFirstLast) ||
        (!!userEmail && normalizeName(contact.email) === normalizeName(userEmail))
      );
    });
    return alreadyListed || !userName ? merged : [currentUserContact, ...merged];
  }, [currentUser?.contact_number, currentUser?.designation, currentUser?.email, currentUser?.full_name, currentUser?.id, currentUser?.username, salesContacts]);

  if (!saved || !draft || !project || !totals) {
    return <Typography>Quotation not found. <Link to="/sales/calcsheet/projects">Back</Link></Typography>;
  }
  // Alias for downstream code that previously referenced `quotation` directly.
  // All field/row operations below mutate `draft` (via setDraft); reads see the
  // live draft so totals/labels update immediately as the user types.
  const quotation = draft;
  const recipient = clients.find((c) => c.id === quotation.recipientId);
  const customer = clients.find((c) => c.id === project.customerId);
  const issuer = quotation.kind;
  const isLegacy = quotation.formulaVersion === 'legacy';
  // Awarded quotations are price-locked (server rejects saves with 409) — same
  // read-only treatment as a legacy snapshot. Revise via Duplicate.
  const priceLocked = !!quotation.awardedAt;
  const inputsLocked = isLegacy || priceLocked;
  // Scope category, fixed at quotation creation (see QuotationScopeCategory).
  // 'supply' leaves only B (Components) editable; 'services' leaves only B
  // locked (A and C stay editable); 'both' (default, incl. every quotation
  // predating this field) locks nothing. Combined with isLegacy below, since a
  // legacy quotation is already fully locked regardless of scope.
  const scopeCategory = quotation.scopeCategory ?? 'both';
  const generalReqtsLocked = inputsLocked || scopeCategory === 'supply';
  const componentsLocked = inputsLocked || scopeCategory === 'services';
  const servicesLocked = inputsLocked || scopeCategory === 'supply';
  // Shown as a quick preview on the Terms & Conditions confirmation dialogs
  // (Save / Export PDF) so the team can eyeball the actual Delivery wording —
  // the thing most often left stale, e.g. after enabling the delivery fee —
  // without having to open the Terms & Conditions accordion separately.
  const effectiveDeliveryText = quotation.termsOverrides?.deliveryLines ?? defaultDeliveryText(quotation.deliveryTerms);
  const quotationDate =
    quotation.dateSent
    || quotation.createdAt?.slice(0, 10)
    || todayDateOnly();
  const effectiveExpectedPurchaseDate = resolveExpectedPurchaseDate(
    quotationDate,
    quotation.expectedPurchaseDate,
  );
  const timingComponent = quotation.components.find((line) => line.id === timingComponentId);
  const invalidPurchaseTiming = hasInvalidPurchaseTiming(
    quotationDate,
    quotation.expectedPurchaseDate,
    quotation.components,
  );

  const setField = <K extends keyof Quotation>(k: K, v: any) => {
    if (inputsLocked) return;
    setDraft((d) => d ? ({ ...d, [k]: v } as Quotation) : d);
  };

  const setTermsOverride = (key: keyof NonNullable<Quotation['termsOverrides']>, value: string) => {
    if (inputsLocked) return;
    setDraft((d) => {
      if (!d) return d;
      const existing = d.termsOverrides ?? {};
      const updated = { ...existing, [key]: value || undefined };
      return { ...d, termsOverrides: updated };
    });
  };

  const setProductContingency = (value: number) => {
    if (inputsLocked) return;
    setDraft((d) => {
      if (!d) return d;
      const previous = d.productContingencyPct ?? 0;
      const components = d.components.map((line) => {
        const isOverridden = !!line.contingencyPctOverridden || (line.contingencyPct ?? 0) !== previous;
        return isOverridden
          ? { ...line, contingencyPctOverridden: true }
          : { ...line, contingencyPct: value, contingencyPctOverridden: false };
      });
      return { ...d, productContingencyPct: value, components };
    });
  };

  // Clicking Save opens the "what changed?" prompt rather than saving
  // immediately — confirmSave (below) does the actual save once the user
  // submits it (remark is optional; blank is fine for a quick iteration).
  const openSavePrompt = () => {
    if (!isDirty || saving || invalidPurchaseTiming) {
      if (invalidPurchaseTiming) {
        setToast({
          msg: 'Expected purchase dates cannot be before the quotation date',
          sev: 'warning',
        });
      }
      return;
    }
    setRemarkText('');
    setSaveTermsConfirmed(false);
    setRemarkDialogOpen(true);
  };

  // Export PDF is gated behind the same Terms & Conditions confirmation —
  // opens a small dialog instead of exporting immediately.
  const requestExportPdf = (layout: QuotationPdfLayout = 'standard') => {
    setPendingExportLayout(layout);
    setExportTermsConfirmed(false);
    setExportConfirmOpen(true);
  };

  const confirmExportPdf = () => {
    setExportConfirmOpen(false);
    void exportPdf(pendingExportLayout);
  };

  const confirmSave = async (remark: string) => {
    setRemarkDialogOpen(false);
    setSaving(true);
    try {
      // Send the full draft as the patch — the store's updateQuotation handles
      // PUT to the server and updates local state on success. The remark is
      // attached server-side to the version snapshot of the pre-save state.
      const savedQuotation = await update(quotation.id, draft, remark.trim());
      setDraft(savedQuotation);
      setToast({ msg: 'Saved', sev: 'success' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ msg: `Save failed: ${msg}`, sev: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!saved) return;
    setDraft(saved);
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      setVersions(await fetchQuotationVersions(quotation.id));
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : String(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreVersion = (v: QuotationVersion) => {
    if (isDirty) {
      const proceed = window.confirm(
        'Restoring this version will replace your unsaved draft edits.\n\nContinue?'
      );
      if (!proceed) return;
    }
    // Load the snapshot into the draft, keeping the live identity fields. The
    // user reviews the restored state and clicks Save to make it permanent
    // (which itself snapshots the current state — nothing is ever lost).
    setDraft({ ...v.data, id: quotation.id, projectId: quotation.projectId });
    setHistoryOpen(false);
    setToast({ msg: 'Version loaded into the editor — review and click Save to keep it', sev: 'info' });
  };

  const handleDeleteVersion = async (v: QuotationVersion) => {
    const proceed = window.confirm('Permanently delete this saved snapshot? This cannot be undone.');
    if (!proceed) return;
    setDeletingVersionId(v.id);
    try {
      await deleteQuotationVersion(quotation.id, v.id);
      setVersions((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ msg: `Failed to delete snapshot: ${msg}`, sev: 'warning' });
    } finally {
      setDeletingVersionId(null);
    }
  };

  const handleOpenDuplicate = () => {
    if (isDirty) {
      const proceed = window.confirm(
        'You have unsaved changes. Duplicating now uses the last saved version — your draft edits will be lost.\n\nDuplicate anyway?'
      );
      if (!proceed) return;
    }
    setDuplicateOpen(true);
  };

  // Section A — General Requirements
  const nextCode = (prefix: string, existing: { code?: string }[]) => {
    let max = 0;
    existing.forEach(r => {
      const m = (r.code || '').match(new RegExp(`^${prefix}-(\\d+)$`));
      if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return `${prefix}-${String(max + 10).padStart(4, '0')}`;
  };

  const addGeneral = () =>
    commit('generalReqts', [
      ...quotation.generalReqts,
      { id: id(), code: nextCode('A', quotation.generalReqts), description: '', unitPrice: 0, qty: 1, uom: 'lot' },
    ] as GeneralReqLine[]);

  const generalCols: Column<GeneralReqLine>[] = [
    { key: 'code', card: 'eyebrow', label: 'Code', width: 90, mono: true },
    { key: 'description', card: 'title', label: 'Description', multiline: true },
    { key: 'qty', card: 'summary', label: 'Qty', width: 70, type: 'number', align: 'right' },
    { key: 'uom', label: 'UOM', width: 70 },
    { key: 'unitPrice', card: 'summary', label: 'Unit Price', width: 120, type: 'number', align: 'right', step: 0.01 },
    // Shows the global General Req. markup (greyed) until a per-line override is
    // typed; clearing the cell falls back to the global again.
    { key: 'markupPct', label: 'Markup %', width: 80, type: 'number', align: 'right', step: 0.01, nullable: true, placeholder: String(quotation.generalReqMarkupPct || 0) },
    { key: 'total', card: 'total', label: 'Total', width: 130, align: 'right',
      render: (r) => {
        const base = lineGeneralTotal(r);
        const markup = r.markupPct != null ? r.markupPct : (quotation.generalReqMarkupPct || 0);
        return <Box sx={{ fontFamily: 'monospace' }}>{PHP(base * (1 + markup / 100))}</Box>;
      } },
  ];

  // Section B — Components
  const addComponent = () =>
    commit('components', [
      ...quotation.components,
      { id: id(), code: nextCode('B', quotation.components), description: '', brand: '', partNo: '',
        qty: 1, uom: 'pc', unitCost: 0, forex: 1, contingencyPct: quotation.productContingencyPct ?? 0, contingencyPctOverridden: false, discountPct: 0 },
    ] as ComponentLine[]);

  const addFromCatalog = (pickedItems: PricelistItem[]) => {
    let nextNum = 10;
    const nums = quotation.components
      .map((c) => parseInt(c.code.replace(/^B-/, ''), 10))
      .filter((n) => !isNaN(n));
    if (nums.length) nextNum = Math.max(...nums) + 10;

    const newLines: ComponentLine[] = pickedItems.map((item, idx) => ({
      id: id(),
      code: `B-${String(nextNum + idx * 10).padStart(4, '0')}`,
      description: `${item.description} [${item.catalogNo}]`,
      brand: item.brand,
      partNo: item.catalogNo,
      qty: 1,
      uom: 'pc',
      unitCost: item.sellingPrice,
      forex: 1,
      contingencyPct: quotation.productContingencyPct ?? 0,
      contingencyPctOverridden: false,
      discountPct: 0,
    }));
    commit('components', [...quotation.components, ...newLines] as ComponentLine[]);
  };

  // Rows arrive with blank codes (see InstallationWorkDialog) — commit()'s
  // renumber step assigns the final sequential B-#### codes.
  // …under an "INSTALLATION WORK" section header (created the first time,
  // reused on later adds — see withInstallationRows).
  const addFromInstallationWork = (rows: ComponentLine[]) => {
    const hadHeader = quotation.components.some((c) => c.isHeader && c.description.trim().toUpperCase() === INSTALLATION_HEADER);
    commit('components', withInstallationRows(quotation.components, rows, () => ({
      id: id(), code: '', description: INSTALLATION_HEADER, brand: '', partNo: '',
      qty: 0, uom: '', unitCost: 0, forex: 1, contingencyPct: 0, discountPct: 0,
      isHeader: true,
    } as ComponentLine)));
    setInstallWorkOpen(false);
    setToast({ msg: `Added ${rows.length} item${rows.length === 1 ? '' : 's'} from Installation Work ${hadHeader ? 'under' : 'with'} the "${INSTALLATION_HEADER}" header — review and Save.`, sev: 'success' });
  };

  // Siemens PLC configurator rows, each group under its own header: "PLC —
  // SIEMENS S7-…", "TERMINAL BLOCKS & RELAYS" and "WIRES".
  const addFromPlc = (sections: PlcSubmitSection[]) => {
    const components = sections.reduce((comps, { header, rows }) => withSectionRows(comps, rows, header, () => ({
      id: id(), code: '', description: header, brand: '', partNo: '',
      qty: 0, uom: '', unitCost: 0, forex: 1, contingencyPct: 0, discountPct: 0,
      isHeader: true,
    } as ComponentLine)), quotation.components);
    commit('components', components);
    setPlcOpen(false);
    setDesigoOpen(false);
    setPanelOpen(false);
    const count = sections.reduce((n, g) => n + g.rows.length, 0);
    setToast({ msg: `Added ${count} item${count === 1 ? '' : 's'} under ${sections.map((g) => `"${g.header}"`).join(', ')} — review and Save.`, sev: 'success' });
  };

  const addFromHistory = (selection: ProductHistoryAddSelection) => {
    const line = createHistoricalComponentLine(
      selection,
      quotation.productContingencyPct ?? 0,
      nextCode('B', quotation.components),
      id(),
    );
    commit('components', [...quotation.components, line] as ComponentLine[]);
  };

  // Shift+click extends the selection across the rows in between (see
  // rangeSelectHandler above) — bound once per render so every row's
  // checkbox shares the same anchor.
  const compSelectClick = rangeSelectHandler(quotation.components, selectedCompIds, compSelectAnchor, setSelectedCompIds);
  const compCols: Column<ComponentLine>[] = [
    { key: '_select', label: selectAllHeaderCheckbox(quotation.components, selectedCompIds, compSelectAnchor, setSelectedCompIds), width: 36, render: (r, idx) => (
      <Checkbox size="small" sx={{ p: 0 }}
        checked={selectedCompIds.has(r.id)}
        onClick={compSelectClick(idx)}
        title="Click to select/deselect · Shift+click to apply to the whole range"
      />
    ) },
    { key: 'code', card: 'eyebrow', label: 'Code', width: 90, mono: true },
    { key: 'description', card: 'title', label: 'Description', width: 260, render: (r, idx) => (
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
        <TextField value={r.description ?? ''} onChange={(e) => updateRow('components', idx, 'description', e.target.value)}
          variant="standard" fullWidth disabled={inputsLocked} multiline minRows={1} sx={{ minWidth: 0, flex: 1 }}
          InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
          inputProps={{ style: { padding: '6px 4px' } }} />
        {r.historicalPriceSource && (
          <HistoricalSourceChip source={r.historicalPriceSource} />
        )}
        <InternalNoteButton note={r.internalNote} lineLabel={r.description || r.code} disabled={inputsLocked}
          onClick={() => setNoteTarget({ section: 'components', id: r.id })} />
      </Stack>
        {r.group && (
          <Tooltip title={groupDisplayMode(r.group) === 'lot'
            ? `"${r.group}" prints as one 1.00 LOT line — click to show each item's qty instead`
            : `"${r.group}" prints each item's qty (one combined price) — click to collapse to 1.00 LOT`}>
            <Chip
              label={`${r.group} · ${groupDisplayMode(r.group) === 'lot' ? 'LOT' : 'itemized'}`}
              size="small"
              color="warning"
              variant={groupDisplayMode(r.group) === 'lot' ? 'outlined' : 'filled'}
              onClick={() => toggleGroupDisplay(r.group!)}
              onDelete={() => ungroupComp(r.id)}
              disabled={inputsLocked}
              sx={{ height: 20, cursor: 'pointer', alignSelf: 'flex-start', maxWidth: '100%', '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
            />
          </Tooltip>
        )}
      </Stack>
    ) },
    { key: 'brand', card: 'summary', label: 'Brand', width: 90 },
    { key: 'partNo', label: 'Part No.', width: 100 },
    { key: 'qty', card: 'summary', label: 'Qty', width: 60, type: 'number', align: 'right' },
    { key: 'uom', label: 'UOM', width: 60 },
    { key: 'unitCost', card: 'summary', label: 'Unit Cost', width: 110, type: 'number', align: 'right', step: 0.01 },
    { key: 'forex', label: 'FX', width: 60, type: 'number', align: 'right', step: 0.0001 },
    { key: 'contingencyPct', label: 'Cont %', width: 80, type: 'number', align: 'right', step: 0.01 },
    // Shows the global Product markup (greyed) until a per-line override is
    // typed; clearing the cell falls back to the global again.
    { key: 'markupPct', label: 'Markup %', width: 80, type: 'number', align: 'right', step: 0.01, nullable: true, placeholder: String(quotation.productMarkupPct || 0) },
    // Same pattern as Markup % — shows the quotation's EWT % (greyed) until a
    // per-line override is typed. IOCT-only in practice (see Quotation.ewtPct);
    // never printed on the PDF/Excel.
    { key: 'ewtPct', label: 'EWT %', width: 70, type: 'number', align: 'right', step: 0.01, nullable: true, placeholder: String(quotation.ewtPct || 0) },
    { key: 'leadTimeDays', label: 'Lead Time', width: 90, type: 'number', align: 'right', min: 0 },
    {
      key: '_timing',
      label: '',
      width: 42,
      render: (row) => (
        <ComponentTimingAction
          componentDescription={row.description || row.code}
          expectedPurchaseDate={row.expectedPurchaseDate}
          invalid={Boolean(
            row.expectedPurchaseDate && row.expectedPurchaseDate < quotationDate
          )}
          disabled={inputsLocked}
          onClick={() => setTimingComponentId(row.id)}
        />
      ),
    },
    { key: 'optional', label: 'Optional', width: 70, align: 'center', render: (r, idx) => (
      <Checkbox size="small" sx={{ p: 0 }}
        checked={!!r.optional}
        disabled={inputsLocked}
        onChange={(e) => updateRow('components', idx, 'optional', e.target.checked)}
        title="Optional item — priced for reference, not included in the contract total"
      />
    ) },
    { key: 'sellPrice', label: 'Selling/u', width: 110, align: 'right',
      render: (r) => <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: r.optional ? 'text.disabled' : undefined }}>{PHP(componentSellingUnit(r, quotation.productMarkupPct, quotation.ewtPct))}</Box> },
    { key: 'total', card: 'total', label: 'Total', width: 130, align: 'right',
      render: (r) => (
        <Box sx={{ fontFamily: 'monospace', fontWeight: 500, color: r.optional ? 'text.disabled' : undefined, fontStyle: r.optional ? 'italic' : undefined }}>
          {PHP(componentLineTotal(r, quotation.productMarkupPct, quotation.ewtPct))}{r.optional ? ' *' : ''}
        </Box>
      ) },
  ];

  // Section C — Services
  const perLinePricing = quotation.servicesFromManpower && !!quotation.servicesPerLinePricing;
  const teamDailyRate = manpowerDailyRate(quotation.manpower);
  // Per-line pricing formula: QTY × Unit Price × (markup × EWT). `qty` falls
  // back to the old `days` field (pre-QTY/UOM/Unit Price quotations) and
  // `unitPrice` falls back to the shared team daily rate when unset — same
  // override pattern as markupPct falling back to the global Labor Markup %.
  const svcQty = (row: ServiceLine) => row.qty ?? row.days ?? 0;
  const svcUnitPrice = (row: ServiceLine) => row.unitPrice ?? teamDailyRate;
  const addService = () =>
    commit('services', [
      ...quotation.services,
      { id: id(), code: nextCode('C', quotation.services), description: '', amount: 0, ...(perLinePricing ? { qty: 0, uom: '' } : {}) },
    ] as ServiceLine[]);

  const updateServiceRow = (idx: number, key: keyof ServiceLine, value: any) => {
    const list = [...quotation.services];
    const row = { ...list[idx], [key]: value };
    // Amount is the single source of truth (calc + PDF/Excel print it directly),
    // so QTY/Unit Price/markup edits recompute it. EWT (IOCT-only, invisible on
    // the PDF) rides along as an extra factor — see Quotation.ewtPct. Guarded
    // on the effective unit price > 0: with no valid cost basis (no per-line
    // Unit Price AND no manpower rate) there's nothing to derive, so leave a
    // manually-typed Amount alone instead of clobbering it to 0.
    if (perLinePricing && svcUnitPrice(row) > 0 && (key === 'qty' || key === 'unitPrice' || key === 'markupPct')) {
      const mult = (1 + (((row.markupPct ?? quotation.laborMarkupPct) || 0) / 100)) * (1 + ((quotation.ewtPct || 0) / 100));
      row.amount = svcQty(row) * svcUnitPrice(row) * mult;
    }
    list[idx] = row;
    setField('services', list);
  };

  const applyGroup = () => {
    if (!groupLabel.trim()) return;
    if (groupDialogOpen === 'services' && selectedSvcIds.size >= 2) {
      setField('services', quotation.services.map((s) =>
        selectedSvcIds.has(s.id) ? { ...s, group: groupLabel.trim() } : s,
      ));
      setSelectedSvcIds(new Set());
    } else if (groupDialogOpen === 'components' && selectedCompIds.size >= 2) {
      setField('components', quotation.components.map((c) =>
        selectedCompIds.has(c.id) ? { ...c, group: groupLabel.trim() } : c,
      ));
      setSelectedCompIds(new Set());
    }
    setGroupDialogOpen(false);
    setGroupLabel('');
  };
  const applySubheader = () => {
    const label = subheaderLabel.trim();
    if (!label) return;
    if (subheaderDialogOpen === 'services' && selectedSvcIds.size) {
      const firstId = quotation.services.find((s) => selectedSvcIds.has(s.id))?.id;
      setField('services', quotation.services.map((s) =>
        s.id === firstId ? { ...s, subheader: label } : s,
      ));
      setSelectedSvcIds(new Set());
    } else if (subheaderDialogOpen === 'components' && selectedCompIds.size) {
      const firstId = quotation.components.find((c) => selectedCompIds.has(c.id))?.id;
      setField('components', quotation.components.map((c) =>
        c.id === firstId ? { ...c, subheader: label } : c,
      ));
      setSelectedCompIds(new Set());
    }
    setSubheaderDialogOpen(false);
    setSubheaderLabel('');
  };
  const ungroupSvc = (svcId: string) => {
    setField('services', quotation.services.map((s) => s.id === svcId ? { ...s, group: undefined } : s));
  };
  const ungroupComp = (compId: string) => {
    setField('components', quotation.components.map((c) => c.id === compId ? { ...c, group: undefined } : c));
  };

  // Section-header rows — label-only lines that break the BOM into named
  // sections (e.g. "FIELD INSTRUMENTS") on screen and on export. Carry no
  // qty/cost and are excluded from every totals calculation (see calc.ts).
  const insertHeaderComponent = (idx: number, position: 'above' | 'below') => {
    const insertAt = position === 'above' ? idx : idx + 1;
    const list = [...quotation.components];
    list.splice(insertAt, 0, {
      id: id(), code: '', description: '', brand: '', partNo: '',
      qty: 0, uom: '', unitCost: 0, forex: 1, contingencyPct: 0, discountPct: 0,
      isHeader: true,
    } as ComponentLine);
    commit('components', list);
  };
  // Child (second-level) header — same label-only, excluded-from-totals row as
  // insertHeaderComponent, just nested under a preceding header (see
  // ComponentLine.isChildHeader).
  const insertChildHeaderComponent = (idx: number, position: 'above' | 'below') => {
    const insertAt = position === 'above' ? idx : idx + 1;
    const list = [...quotation.components];
    list.splice(insertAt, 0, {
      id: id(), code: '', description: '', brand: '', partNo: '',
      qty: 0, uom: '', unitCost: 0, forex: 1, contingencyPct: 0, discountPct: 0,
      isChildHeader: true,
    } as ComponentLine);
    commit('components', list);
  };

  const clearSelectedSubheaders = (section: 'components' | 'services') => {
    const selected = section === 'components' ? selectedCompIds : selectedSvcIds;
    setField(section, quotation[section].map((line) => selected.has(line.id) ? { ...line, subheader: undefined } : line));
  };

  const getComponentContextMenu = (row: ComponentLine, idx: number): ContextMenuItem[] => {
    if (inputsLocked) return [];
    const items: ContextMenuItem[] = [];
    if (!row.isHeader && !row.isChildHeader) {
      items.push({
        label: `Group selected (${selectedCompIds.size})`,
        disabled: selectedCompIds.size < 2,
        onClick: () => setGroupDialogOpen('components'),
      });
      if (row.group) {
        items.push({ label: 'Ungroup this row', onClick: () => ungroupComp(row.id) });
      }
    }
    items.push(
      { label: 'Insert header above', dividerBefore: true, onClick: () => insertHeaderComponent(idx, 'above') },
      { label: 'Insert header below', onClick: () => insertHeaderComponent(idx, 'below') },
      { label: 'Insert child header above', onClick: () => insertChildHeaderComponent(idx, 'above') },
      { label: 'Insert child header below', onClick: () => insertChildHeaderComponent(idx, 'below') },
    );
    items.push({ label: 'Delete row', dividerBefore: true, danger: true, onClick: () => deleteRow('components', idx) });
    return items;
  };

  // Same section-header rows as components (see insertHeaderComponent), for
  // Engineering Services.
  const insertHeaderService = (idx: number, position: 'above' | 'below') => {
    const insertAt = position === 'above' ? idx : idx + 1;
    const list = [...quotation.services];
    list.splice(insertAt, 0, { id: id(), code: '', description: '', amount: 0, isHeader: true } as ServiceLine);
    commit('services', list);
  };
  const insertChildHeaderService = (idx: number, position: 'above' | 'below') => {
    const insertAt = position === 'above' ? idx : idx + 1;
    const list = [...quotation.services];
    list.splice(insertAt, 0, { id: id(), code: '', description: '', amount: 0, isChildHeader: true } as ServiceLine);
    commit('services', list);
  };

  const getServiceContextMenu = (row: ServiceLine, idx: number): ContextMenuItem[] => {
    if (inputsLocked) return [];
    const items: ContextMenuItem[] = [];
    if (!row.isHeader && !row.isChildHeader && perLinePricing) {
      items.push({
        label: `Group selected (${selectedSvcIds.size})`,
        disabled: selectedSvcIds.size < 2,
        onClick: () => setGroupDialogOpen('services'),
      });
      if (row.group) {
        items.push({ label: 'Ungroup this row', onClick: () => ungroupSvc(row.id) });
      }
    }
    items.push(
      { label: 'Insert header above', dividerBefore: true, onClick: () => insertHeaderService(idx, 'above') },
      { label: 'Insert header below', onClick: () => insertHeaderService(idx, 'below') },
      { label: 'Insert child header above', onClick: () => insertChildHeaderService(idx, 'above') },
      { label: 'Insert child header below', onClick: () => insertChildHeaderService(idx, 'below') },
    );
    items.push({ label: 'Delete row', dividerBefore: true, danger: true, onClick: () => deleteRow('services', idx) });
    return items;
  };
  // Per-group export style. Absent = 'lot' (collapse to a single "1.00 LOT"
  // line priced at the group total); 'itemized' shows each member's own qty
  // and UOM while the group keeps one combined price.
  const groupDisplayMode = (group: string): 'lot' | 'itemized' =>
    quotation.componentGroupDisplay?.[group] === 'itemized' ? 'itemized' : 'lot';
  const toggleGroupDisplay = (group: string) => {
    const next = groupDisplayMode(group) === 'lot' ? 'itemized' : 'lot';
    setField('componentGroupDisplay', { ...(quotation.componentGroupDisplay || {}), [group]: next });
  };

  // Shift+click extends the selection across the rows in between (see
  // rangeSelectHandler above) — shared by both svcCols variants below since
  // they select over the same quotation.services array.
  const svcSelectClick = rangeSelectHandler(quotation.services, selectedSvcIds, svcSelectAnchor, setSelectedSvcIds);
  const svcSelectAllHeader = selectAllHeaderCheckbox(quotation.services, selectedSvcIds, svcSelectAnchor, setSelectedSvcIds);
  const svcCols: Column<ServiceLine>[] = perLinePricing
    ? [
        { key: '_select', label: svcSelectAllHeader, width: 36, render: (r, idx) => (
          <Checkbox size="small" sx={{ p: 0 }}
            checked={selectedSvcIds.has(r.id)}
            onClick={svcSelectClick(idx)}
            title="Click to select/deselect · Shift+click to apply to the whole range"
          />
        ) },
        { key: 'code', card: 'eyebrow', label: 'Code', width: 90, mono: true },
        { key: 'description', card: 'title', label: 'Description', render: (r, idx) => (
          <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <TextField value={r.description ?? ''} onChange={(e) => updateServiceRow(idx, 'description', e.target.value)}
              variant="standard" fullWidth disabled={inputsLocked} multiline minRows={1} sx={{ minWidth: 0, flex: 1 }}
              InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }}
              inputProps={{ style: { padding: '6px 4px' } }} />
            <InternalNoteButton note={r.internalNote} lineLabel={r.description || r.code} disabled={inputsLocked}
              onClick={() => setNoteTarget({ section: 'services', id: r.id })} />
            {r.group && <Chip label={r.group} size="small" color="info" variant="outlined" onDelete={() => ungroupSvc(r.id)} sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />}
          </Stack>
        ) },
        { key: 'qty', card: 'summary', label: 'QTY', width: 70, type: 'number', align: 'right', min: 0 },
        { key: 'uom', label: 'UOM', width: 70 },
        // Shows the shared team daily rate (greyed) until a per-line override is
        // typed; editing recomputes the line amount, clearing falls back to the
        // shared rate. Amount = QTY × Unit Price × markup, so the pre-markup
        // budget is QTY × Unit Price — visible inline without scrolling down to
        // the Manpower table.
        { key: 'unitPrice', card: 'summary', label: 'Unit Price', width: 100, type: 'number', align: 'right', step: 0.01, nullable: true, placeholder: String(teamDailyRate || 0) },
        // Shows the global Labor markup (greyed) until a per-line override is typed;
        // editing recomputes the line amount, clearing falls back to the global.
        { key: 'markupPct', label: 'Markup %', width: 80, type: 'number', align: 'right', step: 0.01, nullable: true, placeholder: String(quotation.laborMarkupPct || 0) },
        { key: 'amount', card: 'total', label: 'Amount', width: 140, type: 'number', align: 'right', step: 0.01 },
      ]
    : [
        // No markup column here: lump mode prices from manpower × Labor Markup %,
        // and manual mode's amounts are final prices with no cost basis to mark up.
        { key: '_select', label: svcSelectAllHeader, width: 36, render: (r, idx) => (
          <Checkbox size="small" sx={{ p: 0 }} checked={selectedSvcIds.has(r.id)}
            onClick={svcSelectClick(idx)}
            title="Click to select/deselect · Shift+click to apply to the whole range" />
        ) },
        { key: 'code', card: 'eyebrow', label: 'Code', width: 90, mono: true },
        { key: 'description', card: 'title', label: 'Description', render: (r, idx) => (
          <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <TextField value={r.description ?? ''} onChange={(e) => updateServiceRow(idx, 'description', e.target.value)} variant="standard" fullWidth disabled={inputsLocked} multiline minRows={1} sx={{ minWidth: 0, flex: 1 }}
              InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem' } }} inputProps={{ style: { padding: '6px 4px' } }} />
            <InternalNoteButton note={r.internalNote} lineLabel={r.description || r.code} disabled={inputsLocked}
              onClick={() => setNoteTarget({ section: 'services', id: r.id })} />
          </Stack>
        ) },
        { key: 'amount', card: 'total', label: 'Amount', width: 150, type: 'number', align: 'right', step: 0.01 },
      ];

  const addManpower = () =>
    setField('manpower', [
      ...quotation.manpower,
      { id: id(), role: '', group: 'engineering', headcount: 1, mandays: 0, dailyRate: 0, allowance: 0, presetId: null },
    ] as ManpowerEntry[]);

  const applyPreset = (idx: number, presetId: string) => {
    const list = [...quotation.manpower];
    if (presetId === '__custom__') {
      list[idx] = { ...list[idx], presetId: null };
    } else {
      const p = presets.find((x) => x.id === presetId);
      if (!p) return;
      list[idx] = {
        ...list[idx],
        presetId: p.id,
        role: p.role,
        group: p.group,
        dailyRate: p.dailyRate,
        allowance: p.allowance,
      };
    }
    setField('manpower', list);
    recalcServiceAmounts(list);
  };

  const engineeringPresets = presets.filter((p) => p.group === 'engineering');
  const laborPresets = presets.filter((p) => p.group === 'labor');

  const mpCols: Column<ManpowerEntry>[] = [
    { key: 'role', card: 'title', label: 'Role', width: 240,
      render: (r, idx) => {
        const isCustom = !r.presetId;
        // A saved presetId may not resolve to a currently-loaded preset (preset
        // reseeded with new ids, or presets not loaded). Without a matching
        // MenuItem the Select renders blank AND the custom-name fallback is
        // hidden, so the stored role would vanish — render a fallback option
        // that shows r.role so it's always visible.
        const presetResolves = !r.presetId || presets.some((p) => p.id === r.presetId);
        return (
          <Stack direction="column" spacing={0.5} sx={{ py: 0.5 }}>
            <TextField
              select
              value={r.presetId ?? '__custom__'}
              onChange={(e) => applyPreset(idx, e.target.value)}
              variant="standard"
              InputProps={{ disableUnderline: true, sx: { fontSize: '0.8125rem', fontWeight: 500 } }}
              fullWidth
            >
              {!presetResolves && (
                <MenuItem value={r.presetId as string}>{r.role || '(unknown role)'}</MenuItem>
              )}
              <ListSubheader>Engineering / Automation</ListSubheader>
              {engineeringPresets.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.role}</MenuItem>
              ))}
              <ListSubheader>Laborers</ListSubheader>
              {laborPresets.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.role}</MenuItem>
              ))}
              <Divider />
              <MenuItem value="__custom__">— Custom… —</MenuItem>
            </TextField>
            {isCustom && (
              <TextField
                value={r.role}
                onChange={(e) => updateRow('manpower', idx, 'role', e.target.value)}
                placeholder="Custom role name"
                variant="standard"
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' } }}
                fullWidth
              />
            )}
          </Stack>
        );
      },
    },
    { key: 'group', label: 'Group', width: 110,
      render: (r, idx) => (
        <TextField select value={r.group} onChange={(e) => updateRow('manpower', idx, 'group', e.target.value)} variant="standard" InputProps={{ disableUnderline: true, sx: { fontSize: '0.75rem' } }} fullWidth disabled={!!r.presetId}>
          <MenuItem value="engineering">Engineering</MenuItem>
          <MenuItem value="labor">Labor</MenuItem>
        </TextField>
      ) },
    { key: 'headcount', card: 'summary', label: 'Pax', width: 60, type: 'number', align: 'right' },
    { key: 'mandays', card: 'summary', label: 'Mandays', width: 80, type: 'number', align: 'right' },
    { key: 'dailyRate', card: 'summary', label: 'Daily Rate', width: 110, type: 'number', align: 'right', step: 0.01 },
    { key: 'allowance', label: 'Allowance', width: 100, type: 'number', align: 'right', step: 0.01 },
    { key: 'cost', card: 'total', label: 'Cost', width: 130, align: 'right',
      render: (r) => <Box sx={{ fontFamily: 'monospace' }}>{PHP(manpowerCost(r))}</Box> },
  ];

  type Section = 'generalReqts' | 'components' | 'services' | 'manpower';
  const codePrefixOf = (s: Section): 'A' | 'B' | 'C' | null =>
    s === 'generalReqts' ? 'A' : s === 'components' ? 'B' : s === 'services' ? 'C' : null;

  const renumber = <T extends { code?: string }>(rows: T[], prefix: 'A' | 'B' | 'C'): T[] =>
    rows.map((r, i) => ({ ...r, code: `${prefix}-${String((i + 1) * 10).padStart(4, '0')}` }));

  const commit = (section: Section, list: any[]) => {
    const prefix = codePrefixOf(section);
    setField(section, prefix ? renumber(list, prefix) : list);
  };

  // When manpower changes and per-line pricing is on, recalculate service
  // amounts for any scope item that's priced off the shared team rate (i.e.
  // has no per-line Unit Price override) and has a QTY set.
  const recalcServiceAmounts = (manpowerRows: ManpowerEntry[]) => {
    if (!perLinePricing) return;
    const rate = manpowerDailyRate(manpowerRows);
    // No valid cost basis yet (e.g. mid-edit, or manpower cleared) — leave
    // whatever amounts are already there instead of zeroing them out.
    if (rate <= 0) return;
    const mult = 1 + (quotation.laborMarkupPct || 0) / 100;
    const updated = quotation.services.map((s) =>
      s.unitPrice == null && svcQty(s) > 0 ? { ...s, amount: svcQty(s) * rate * mult } : s,
    );
    if (updated.some((s, i) => s.amount !== quotation.services[i].amount)) {
      setField('services', updated);
    }
  };

  const updateRow = (section: Section, idx: number, key: any, value: any) => {
    const list = [...(quotation as any)[section]];
    list[idx] = {
      ...list[idx],
      [key]: value,
      ...(section === 'components' && key === 'contingencyPct' ? { contingencyPctOverridden: true } : {}),
    };
    // Don't renumber on field edits — only on structural changes (add/delete/reorder)
    setField(section, list);
    if (section === 'manpower' && ['headcount', 'dailyRate', 'allowance'].includes(key)) {
      recalcServiceAmounts(list);
    }
  };
  const deleteRow = (section: Section, idx: number) => {
    const list = [...(quotation as any)[section]];
    list.splice(idx, 1);
    commit(section, list);
    if (section === 'manpower') {
      recalcServiceAmounts(list);
    }
  };
  const reorderRows = (section: Section, newRows: any[]) => {
    commit(section, newRows);
  };

  const isCustomPayment = !PAYMENT_TERM_OPTIONS.includes(quotation.paymentTerms);
  const generalReqtsExportQty = Math.max(1, quotation.generalReqtsExportQty || 1);
  const generalReqtsExportUnitPrice = totals.generalReqtsSubtotal / generalReqtsExportQty;
  const engineeringServicesQty = Math.max(1, quotation.engineeringServicesQty || 1);
  const engineeringServicesUnitPrice = totals.servicesSubtotal / engineeringServicesQty;

  // OneDrive auto-upload on export. The PDF is always saved locally first;
  // upload to the project's OneDrive folder is best-effort (gated on:
  // corporate config present + signed in + project has a folder linked).
  // (Hook declarations live higher up to satisfy rules-of-hooks ordering.)

  const exportPdf = async (layout: QuotationPdfLayout = 'standard') => {
    try {
      const { blob, filename } = await exportQuotationPdf(
        quotation, project, recipient ?? null, customer ?? null, effectiveSalesContacts, { layout },
      );

      // Use whichever folder is current. After promotion to 'won', the move
      // preserves the drive item id; either field references the same physical
      // folder. Prefer executionFolderId when the project is won, else proposal.
      const folderId = (project.status === 'won' && project.executionFolderId)
        ? project.executionFolderId
        : project.proposalFolderId;

      if (!isCorporateOneDriveConfigured() || !oneDriveSignedIn || !folderId) {
        setToast({ msg: `Saved ${filename}`, sev: 'info' });
        return;
      }

      // Fire upload; show success/failure toast but never block the local save.
      setToast({ msg: `Saved ${filename} · uploading to OneDrive…`, sev: 'info' });
      try {
        const token = await getOneDriveToken();
        if (!token) {
          setToast({ msg: `Saved ${filename} · OneDrive upload skipped (no token)`, sev: 'warning' });
          return;
        }
        const driveId = await resolveCorporateDriveId(token);
        await uploadFileToFolderById(token, driveId, folderId, filename, blob);
        setToast({ msg: `Saved ${filename} · uploaded to OneDrive ✓`, sev: 'success' });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setToast({ msg: `Saved ${filename} · OneDrive upload failed: ${msg}`, sev: 'warning' });
        // eslint-disable-next-line no-console
        console.warn('[OneDrive] PDF upload failed (non-blocking)', err);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ msg: `Export failed: ${msg}`, sev: 'warning' });
    }
  };

  const exportXlsx = () => exportQuotationXlsx(quotation, project, recipient ?? null, customer ?? null);
  // Excel has no confirmation step of its own, so warn first if printed fields look like they hold internal wording.
  const requestExportXlsx = () => {
    if (findInternalLookingText(quotation).length > 0) setXlsxWarnOpen(true);
    else void exportXlsx();
  };

  const noteLine = noteTarget
    ? ((noteTarget.section === 'components' ? quotation.components : quotation.services) as Array<ComponentLine | ServiceLine>)
      .find((l) => l.id === noteTarget.id)
    : undefined;
  const saveInternalNote = (text: string) => {
    if (!noteTarget) return;
    const list = (noteTarget.section === 'components' ? quotation.components : quotation.services) as Array<{ id: string }>;
    const idx = list.findIndex((l) => l.id === noteTarget.id);
    const value = text.trim() || undefined; // an emptied note is removed, not stored as ''
    if (idx >= 0) {
      if (noteTarget.section === 'components') updateRow('components', idx, 'internalNote', value);
      else updateServiceRow(idx, 'internalNote', value);
    }
    setNoteTarget(null);
  };
  const compactPdf = compactLayoutAvailability(
    quotation.generalReqts.length + quotation.components.length + quotation.services.length,
  );

  // Each category's share of the VAT-EX subtotal — helps sanity-check the
  // cost mix (e.g. is Labor too thin relative to Product) while nudging
  // prices to land on a round total.
  const mixPct = (value: number) => (totals.subtotal > 0 ? `${((value / totals.subtotal) * 100).toFixed(1)}%` : '—');

  // Each T&C textbox displays the default wording whenever the underlying
  // override is blank (see the Terms & Conditions accordion below), so a
  // plain truthiness check on termsOverrides would misfire if a user clicks
  // into a field and retypes the default verbatim. Compare against what the
  // default computes to right now instead — only flag genuine edits.
  const to = quotation.termsOverrides;
  const hasCustomTerms = !!to && (
    (!!to.scopeOfWork && to.scopeOfWork !== DEFAULT_SCOPE_OF_WORK) ||
    !!to.exclusions ||
    (!!to.basisOfProposal && to.basisOfProposal !== defaultBasisOfProposal(ISSUER_NAMES[quotation.kind])) ||
    (!!to.deliveryLines && to.deliveryLines !== defaultDeliveryText(quotation.deliveryTerms)) ||
    (!!to.warrantyExclusion && to.warrantyExclusion !== DEFAULT_WARRANTY_EXCLUSION) ||
    (!!to.taxStatus && to.taxStatus !== defaultTaxStatusTerm(ISSUER_NAMES[quotation.kind]))
  );

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={`${issuer} → ${recipient?.code ?? '?'}`} color={issuer === 'IOCT' ? 'primary' : 'secondary'} />
            {scopeCategory !== 'both' && (
              <Chip
                size="small"
                label={scopeCategory === 'supply' ? 'Supply only' : 'Services only'}
                variant="outlined"
                title="Scope is fixed at creation — see the locked sections below"
              />
            )}
            {isLegacy && (
              <Chip
                size="small"
                icon={<HistoryIcon />}
                label="Legacy"
                color="warning"
                variant="outlined"
              />
            )}
            {priceLocked && (
              <Chip size="small" icon={<LockIcon />} label="Awarded · price locked" color="success" />
            )}
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
              {quotationRefNo(project.code, recipient?.code, quotation.revision)}
            </Typography>
          </Stack>
          <Typography variant="h5">{project.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            Recipient: {recipient?.name ?? '— not set —'}
          </Typography>
          {isDirty && !inputsLocked && (
            <Chip
              size="small"
              label="Unsaved changes"
              color="warning"
              variant="outlined"
              sx={{ alignSelf: 'flex-start', mt: 0.5 }}
            />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {!inputsLocked && (
            <>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                color="primary"
                onClick={openSavePrompt}
                disabled={!isDirty || saving || invalidPurchaseTiming}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                startIcon={<UndoIcon />}
                variant="outlined"
                color="inherit"
                size="small"
                onClick={handleDiscard}
                disabled={!isDirty || saving}
              >
                Discard
              </Button>
            </>
          )}
          <Button
            startIcon={<PictureAsPdfIcon />}
            variant={isLegacy ? 'contained' : 'outlined'}
            onClick={() => requestExportPdf('standard')}
            disabled={isDirty}
            title={isDirty ? 'Save changes before exporting' : undefined}
          >
            Export PDF
          </Button>
          {!isLegacy && compactPdf.available && (
            <Button
              startIcon={<PictureAsPdfIcon />}
              variant="contained"
              color="primary"
              onClick={() => requestExportPdf('compact')}
              disabled={isDirty}
              title={isDirty ? 'Save changes before exporting' : 'Tighter print layout for a short quotation'}
            >
              Compact PDF (1 page)
            </Button>
          )}
          <Button
            startIcon={<GridOnIcon />}
            variant="outlined"
            onClick={requestExportXlsx}
            disabled={isDirty}
            title={isDirty ? 'Save changes before exporting' : undefined}
          >
            Export Excel
          </Button>
          <Button
            startIcon={<ContentCopyIcon />}
            variant="outlined"
            color="inherit"
            onClick={handleOpenDuplicate}
            title="Duplicate this quotation, optionally into another project"
          >
            Duplicate
          </Button>
          {!inputsLocked && (
            <Button
              startIcon={<LibraryAddIcon />}
              variant="outlined"
              color="inherit"
              onClick={() => setCopyOpen(true)}
              title="Copy general requirements, components, services or manpower from another quotation"
            >
              Copy from…
            </Button>
          )}
          {!inputsLocked && (
            <Button
              startIcon={<BookmarksIcon />}
              variant="outlined"
              color="inherit"
              onClick={() => setLibraryOpen(true)}
              title="Insert reusable scope from the shared library, or save this quotation's scope to it"
            >
              Scope Library
            </Button>
          )}
          <Button
            startIcon={<HistoryIcon />}
            variant="outlined"
            color="inherit"
            onClick={openHistory}
            title="View previously saved versions of this quotation"
          >
            History
          </Button>
          <Tooltip title={showFloatingTotals ? 'Hide floating totals' : 'Show a draggable totals panel while you edit prices'}>
            <Button
              startIcon={showFloatingTotals ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              variant={showFloatingTotals ? 'contained' : 'outlined'}
              color="inherit"
              onClick={toggleFloatingTotals}
            >
              Totals
            </Button>
          </Tooltip>
          <Button component={Link} to={`/sales/calcsheet/projects/${project.id}`} variant="text" size="small">← Project</Button>
        </Stack>
      </Stack>

      {priceLocked && (
        <Alert
          severity="success"
          icon={<LockIcon />}
          action={
            <Button color="inherit" size="small" startIcon={<ContentCopyIcon />} onClick={handleOpenDuplicate}>
              Duplicate to revise
            </Button>
          }
        >
          <AlertTitle>Awarded — price locked</AlertTitle>
          This is the quotation the project was awarded on
          {quotation.awardedAt ? ` (${new Date(quotation.awardedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${quotation.awardedBy ? `, ${quotation.awardedBy}` : ''})` : ''}.
          Its price and budget are frozen so the execution project and expense monitoring stay aligned.
          For a change order, duplicate it into a new revision; an admin can unlock it from the proposal page.
        </Alert>
      )}

      {isLegacy && (
        <Alert
          severity="warning"
          icon={<HistoryIcon />}
          action={
            <Button color="inherit" size="small" startIcon={<ContentCopyIcon />} onClick={handleOpenDuplicate}>
              Duplicate to revise
            </Button>
          }
        >
          <AlertTitle>Legacy snapshot — totals are frozen</AlertTitle>
          This quotation was imported from an Excel calcsheet that used the previous formulation
          (additive contingency/discount on components, per-role contingency on labor). Inputs are
          locked to preserve the historical record. To create a new revision under the current
          formulation, click <strong>Duplicate to revise</strong>.
          {quotation.importedFrom?.sourceFile && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', color: 'text.secondary' }}>
              Source: {quotation.importedFrom.sourceFile}
              {quotation.importedFrom.pdfFilename ? ` · PDF: ${quotation.importedFrom.pdfFilename}` : ''}
              {quotation.importedFrom.originalCode ? ` · Original code: ${quotation.importedFrom.originalCode}` : ''}
            </Typography>
          )}
        </Alert>
      )}

      {/* Header / metadata */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <TextField
            select
            label="Recipient"
            value={quotation.recipientId ?? ''}
            onChange={(e) => {
              if (inputsLocked) return;
              const newRecipientId = e.target.value || null;
              const newClient = clients.find((c) => c.id === newRecipientId) ?? null;
              const newPrimary = newClient?.contacts?.find((c) => c.isPrimary) ?? newClient?.contacts?.[0] ?? null;
              update(quotation.id, {
                recipientId: newRecipientId,
                contactId: newPrimary?.id,
              } as any);
            }}
            disabled={inputsLocked}
          >
            {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Contact"
            value={(() => {
              const explicit = quotation.contactId && recipient?.contacts?.find((c) => c.id === quotation.contactId);
              if (explicit) return quotation.contactId;
              const fallback = recipient?.contacts?.find((c) => c.isPrimary) ?? recipient?.contacts?.[0];
              return fallback?.id ?? '';
            })()}
            onChange={(e) => setField('contactId', e.target.value || undefined)}
            disabled={inputsLocked || !recipient || !recipient.contacts || recipient.contacts.length === 0}
            helperText={(() => {
              const c = recipient?.contacts?.find((x) => x.id === quotation.contactId)
                ?? recipient?.contacts?.find((x) => x.isPrimary)
                ?? recipient?.contacts?.[0];
              return c ? [c.email, c.phone].filter(Boolean).join(' · ') : 'No contacts';
            })()}
          >
            {(recipient?.contacts ?? []).map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}{c.isPrimary ? ' ★' : ''}{c.position ? ` — ${c.position}` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Revision" value={quotation.revision} onChange={(e) => setField('revision', e.target.value)} disabled={inputsLocked} />
          <TextField
            label="Date Sent"
            type="date"
            value={quotation.dateSent || todayDateOnly()}
            onChange={(e) => setField('dateSent', e.target.value || undefined)}
            disabled={inputsLocked}
            InputLabelProps={{ shrink: true }}
            helperText={quotation.dateSent ? 'User-defined export date' : 'Defaults to export date'}
          />
          <NumField label="Validity (days)" value={quotation.validityDays} onChange={(v) => setField('validityDays', v)} integer disabled={inputsLocked} />
          <NumField label="Warranty (months)" value={quotation.warrantyMonths} onChange={(v) => setField('warrantyMonths', v)} integer disabled={inputsLocked} />
          <Box sx={{ gridColumn: 'span 2' }}>
            <TextField
              select
              label="Payment Terms"
              size="small"
              fullWidth
              disabled={inputsLocked}
              value={isCustomPayment ? CUSTOM_PAYMENT : quotation.paymentTerms}
              onChange={(e) => setField('paymentTerms', e.target.value === CUSTOM_PAYMENT ? '' : e.target.value)}
            >
              {PAYMENT_TERM_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
              <Divider />
              <MenuItem value={CUSTOM_PAYMENT}>— Custom… —</MenuItem>
            </TextField>
            {isCustomPayment && (
              <TextField
                label="Custom Payment Terms"
                size="small"
                fullWidth
                value={quotation.paymentTerms}
                onChange={(e) => setField('paymentTerms', e.target.value)}
                disabled={inputsLocked}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
          {(() => {
            // Under ACTI, prefer each contact's ACTI-side title/email (their
            // designation under the ACTI partnership) so the editor previews
            // what pdfExport.tsx will actually render on the signature block.
            const isActi = quotation.kind === 'ACTI';
            const positionFor = (c: SalesContact | undefined) => (isActi ? c?.actiPosition : undefined) || c?.position;
            const emailFor = (c: SalesContact | undefined) => (isActi ? c?.actiEmail : undefined) || c?.email;
            const staffOption = (props: any, option: string) => {
              const c = effectiveSalesContacts.find((x) => x.name === option);
              const sub = [positionFor(c), c?.phone, emailFor(c)].filter(Boolean).join(' · ');
              return (
                <li {...props} key={option}>
                  <Box>
                    <Typography variant="body2">{option}</Typography>
                    {sub && (
                      <Typography variant="caption" color="text.secondary">{sub}</Typography>
                    )}
                  </Box>
                </li>
              );
            };
            const helperFor = (name: string | undefined) => {
              const c = findSalesContact(name, effectiveSalesContacts);
              return c ? [positionFor(c), c.phone, emailFor(c)].filter(Boolean).join(' · ') : ' ';
            };
            return (
              <>
                <Autocomplete
                  freeSolo
                  options={effectiveSalesContacts.map((c) => c.name).filter(Boolean)}
                  value={quotation.preparedBy ?? ''}
                  onChange={(_, v) => setField('preparedBy', v ?? '')}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setField('preparedBy', v); }}
                  disabled={inputsLocked}
                  renderOption={staffOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Commercially prepared by"
                      placeholder="Pick a team member or type a custom name"
                      helperText={helperFor(quotation.preparedBy)}
                    />
                  )}
                />
                <Tooltip title="Overrides the auto-resolved job title shown in the PDF signature block. Leave blank to use the title from the team member's contact record.">
                  <TextField
                    label="Job Title (PDF signature)"
                    size="small"
                    fullWidth
                    disabled={inputsLocked}
                    value={quotation.preparedByTitle ?? ''}
                    onChange={(e) => setField('preparedByTitle', e.target.value || undefined)}
                    placeholder={(() => { const c = findSalesContact(quotation.preparedBy, effectiveSalesContacts); return positionFor(c) || 'e.g. Sales Engineer'; })()}
                    helperText="Leave blank to auto-resolve from team member record"
                  />
                </Tooltip>
                <Autocomplete
                  freeSolo
                  options={effectiveSalesContacts.map((c) => c.name).filter(Boolean)}
                  value={quotation.authorizedBy ?? ''}
                  onChange={(_, v) => setField('authorizedBy', v ?? '')}
                  onInputChange={(_, v, reason) => { if (reason === 'input') setField('authorizedBy', v); }}
                  disabled={inputsLocked}
                  renderOption={staffOption}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Authorized by"
                      placeholder="Pick a team member or type a custom name"
                      helperText={helperFor(quotation.authorizedBy)}
                    />
                  )}
                />
              </>
            );
          })()}
        </Box>
      </Paper>

      {/* Terms & Conditions overrides */}
      {!isLegacy && (
        <Accordion disableGutters elevation={1} sx={{ '&:before': { display: 'none' }, borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Terms &amp; Conditions
              {hasCustomTerms && (
                <Chip label="custom" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: 11 }} />
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <Typography variant="caption" color="text.secondary">
                Each field below starts filled with the current default wording — edit it in place, or clear it entirely to fall back to the default. Whatever's shown here is exactly what prints in the exported PDF.
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!!quotation.pageBreakBeforeTerms}
                    onChange={(e) => setField('pageBreakBeforeTerms', e.target.checked)}
                    disabled={inputsLocked}
                  />
                }
                label={<Typography variant="caption">Start Terms on new PDF page</Typography>}
              />
              {quotation.nonVat && (
                <TextField
                  label="Tax Status clause (printed first, bold)"
                  multiline
                  minRows={2}
                  fullWidth
                  value={quotation.termsOverrides?.taxStatus ?? defaultTaxStatusTerm(ISSUER_NAMES[quotation.kind])}
                  onChange={(e) => setTermsOverride('taxStatus', e.target.value)}
                  helperText="Shown because Non-VAT is on. Replaces the default Tax Status wording."
                />
              )}
              <TextField
                label="Scope of Work"
                multiline
                minRows={3}
                fullWidth
                value={quotation.termsOverrides?.scopeOfWork ?? DEFAULT_SCOPE_OF_WORK}
                onChange={(e) => setTermsOverride('scopeOfWork', e.target.value)}
                helperText="Replaces the default Scope of Work paragraph"
              />

              <TextField
                label="Exclusions"
                multiline
                minRows={2}
                fullWidth
                value={quotation.termsOverrides?.exclusions ?? ''}
                onChange={(e) => setTermsOverride('exclusions', e.target.value)}
                placeholder={'- Civil, structural, and mechanical works\n- Permits, licenses, and government fees\n- Items and services not explicitly listed in the Scope of Work'}
                helperText="Optional — adds an Exclusions section to the PDF, right after Scope of Work. Each line on its own row (start with '- '). Leave blank to omit the section entirely — there is no default text."
              />

              <TextField
                label="Basis of Proposal"
                multiline
                minRows={3}
                fullWidth
                value={quotation.termsOverrides?.basisOfProposal ?? defaultBasisOfProposal(ISSUER_NAMES[quotation.kind])}
                onChange={(e) => setTermsOverride('basisOfProposal', e.target.value)}
                helperText="Replaces the default Basis of Proposal paragraph"
              />

              {(() => {
                const totalScopeDays = quotation.services.reduce((s, l) => s + (l.days || 0), 0);
                const installDays = totalScopeDays > 0 ? totalScopeDays + 5 : 0;
                const maxComponentLead = quotation.components.reduce((m, c) => Math.max(m, c.leadTimeDays || 0), 0);
                const installWeeks = installDays > 0 ? Math.ceil(installDays / 7) : 0;
                const deliveryWeeks = maxComponentLead > 0 ? Math.ceil(maxComponentLead / 7) : 0;
                const parts: string[] = [];
                if (installWeeks > 0) parts.push(`Installation is ${installWeeks} week${installWeeks > 1 ? 's' : ''} (${installDays} days)`);
                if (deliveryWeeks > 0) parts.push(`Delivery of components is ${deliveryWeeks} week${deliveryWeeks > 1 ? 's' : ''} (${maxComponentLead} days)`);
                const suggestedLine1 = parts.length > 0
                  ? parts.join('. ') + ', upon receipt of a technically and commercially clarified purchase order.'
                  : '';
                const effectiveText = quotation.termsOverrides?.deliveryLines ?? defaultDeliveryText(quotation.deliveryTerms);
                const suggestedText = defaultDeliveryText(suggestedLine1);
                return (
                  <TextField
                    label="Delivery — all bullet lines"
                    multiline
                    minRows={3}
                    fullWidth
                    value={effectiveText}
                    onChange={(e) => setTermsOverride('deliveryLines', e.target.value)}
                    helperText={suggestedLine1 && suggestedText !== effectiveText
                      ? <span>Suggested (from scope days / component lead time): {suggestedLine1} <Typography component="span" variant="caption" sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setTermsOverride('deliveryLines', suggestedText)}>Apply</Typography></span>
                      : "Each line on its own row (start with '- '). Clear to fall back to the default DDP wording."}
                  />
                );
              })()}

              <TextField
                label="Warranty exclusion clause"
                multiline
                minRows={2}
                fullWidth
                value={quotation.termsOverrides?.warrantyExclusion ?? DEFAULT_WARRANTY_EXCLUSION}
                onChange={(e) => setTermsOverride('warrantyExclusion', e.target.value)}
                helperText="Replaces the default warranty-exclusion sentence (the coverage sentence above it stays driven by the Warranty months field)"
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Markup, Contingency & Tax */}
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Pricing Controls</Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
              <Tooltip title="Issuer is NON-VAT (percentage tax). No VAT is added; the PDF/Excel show “VAT: N/A (Non-VAT)” and lead the terms with a bold Tax Status clause.">
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!quotation.nonVat}
                      onChange={(e) => setDraft((d) => d ? ({ ...d, nonVat: e.target.checked, ...(e.target.checked ? { vatPct: 0 } : {}) } as Quotation) : d)}
                      disabled={inputsLocked}
                    />
                  }
                  label={<Typography variant="body2">Non-VAT</Typography>}
                />
              </Tooltip>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={quotation.vatPct > 0}
                    onChange={(e) => setField('vatPct', e.target.checked ? 12 : 0)}
                    disabled={inputsLocked || !!quotation.nonVat}
                  />
                }
                label={<Typography variant="body2">Include VAT</Typography>}
              />
              {quotation.vatPct > 0 && (
                <NumField label="VAT %" value={quotation.vatPct} onChange={(v) => setField('vatPct', v)} sx={{ width: 80 }} disabled={inputsLocked} />
              )}
            </Stack>
          </Stack>

          {/* Delivery fee & minimum-order surcharge (opt-in) */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!quotation.deliveryTermsEnabled}
                  onChange={(e) => setField('deliveryTermsEnabled', e.target.checked)}
                  disabled={inputsLocked}
                />
              }
              label={<Typography variant="body2">Charge delivery fee &amp; minimum-order surcharge</Typography>}
            />
            {quotation.deliveryTermsEnabled && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mt: 1 }}>
                  <NumField label="Delivery Fee (₱)" value={quotation.deliveryFee ?? 0} onChange={(v) => setField('deliveryFee', v)} helperText="Base freight/delivery charge" disabled={inputsLocked} sx={{ width: '100%' }} />
                  <NumField label="Min. order (₱)" value={quotation.minOrderThreshold ?? 50000} onChange={(v) => setField('minOrderThreshold', v)} helperText="Below this, add the surcharge" disabled={inputsLocked} sx={{ width: '100%' }} />
                  <NumField label="Small-order surcharge (₱)" value={quotation.smallOrderFee ?? 5000} onChange={(v) => setField('smallOrderFee', v)} helperText="Added when under minimum" disabled={inputsLocked} sx={{ width: '100%' }} />
                </Box>
                {totals.subtotal < (quotation.minOrderThreshold ?? 50000) && (
                  <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    Order subtotal {PHP(totals.subtotal)} is below the {PHP(quotation.minOrderThreshold ?? 50000)} minimum —
                    a {PHP(quotation.smallOrderFee ?? 5000)} small-order surcharge is added to delivery.
                  </Alert>
                )}
              </>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <NumField label="Product Markup %" value={quotation.productMarkupPct} onChange={(v) => setField('productMarkupPct', v)} disabled={componentsLocked} sx={{ width: '100%' }} />
            <NumField label="Product Contingency %" value={quotation.productContingencyPct ?? 0} onChange={setProductContingency} helperText="Default for product rows" disabled={componentsLocked} sx={{ width: '100%' }} />
            <NumField label="General Req. Markup %" value={quotation.generalReqMarkupPct} onChange={(v) => setField('generalReqMarkupPct', v)} disabled={generalReqtsLocked} sx={{ width: '100%' }} />
            <NumField label="Labor Markup %" value={quotation.laborMarkupPct} onChange={(v) => { setField('laborMarkupPct', v); if (perLinePricing) { setField('services', quotation.services.map((s) => { if (svcQty(s) <= 0 || svcUnitPrice(s) <= 0) return s; const mult = (1 + (((s.markupPct ?? v) || 0) / 100)) * (1 + ((quotation.ewtPct || 0) / 100)); return { ...s, amount: svcQty(s) * svcUnitPrice(s) * mult }; })); } }} helperText="Applied on top of manpower cost" disabled={servicesLocked} sx={{ width: '100%' }} />
            <NumField label="Labor Contingency %" value={quotation.globalContingencyPct} onChange={(v) => setField('globalContingencyPct', v)} helperText="Reserve, not applied to pricing" disabled={servicesLocked} sx={{ width: '100%' }} />
            <NumField
              label="Discount %"
              value={quotation.discountPct}
              onChange={(v) => setField('discountPct', v)}
              helperText={
                quotation.discountPct > 0
                  ? `Client PDF shows ${formatDiscountPct(quotation.discountPct)}% (rounded)`
                  : 'Or use Discount ₱ / Target net below'
              }
              disabled={inputsLocked}
              sx={{ width: '100%' }}
            />
            <NumField
              label="EWT %"
              value={quotation.ewtPct ?? 0}
              onChange={(v) => {
                setField('ewtPct', v);
                if (perLinePricing) {
                  setField('services', quotation.services.map((s) => {
                    if (svcQty(s) <= 0 || svcUnitPrice(s) <= 0) return s;
                    const mult = (1 + (((s.markupPct ?? quotation.laborMarkupPct) || 0) / 100)) * (1 + ((v || 0) / 100));
                    return { ...s, amount: svcQty(s) * svcUnitPrice(s) * mult };
                  }));
                }
              }}
              helperText="IOCT-only gross-up, folded into markup — never printed on the PDF/Excel"
              disabled={inputsLocked}
              sx={{ width: '100%' }}
            />
            <TextField
              label="Expected purchase date"
              type="date"
              size="small"
              value={quotation.expectedPurchaseDate ?? ''}
              onChange={(event) => setField('expectedPurchaseDate', event.target.value || undefined)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: quotationDate }}
              error={Boolean(
                quotation.expectedPurchaseDate
                && quotation.expectedPurchaseDate < quotationDate
              )}
              helperText={
                quotation.expectedPurchaseDate && quotation.expectedPurchaseDate < quotationDate
                  ? 'Expected purchase date cannot be before the quotation date'
                  : quotation.expectedPurchaseDate
                    ? 'Used for product price-contingency forecasts'
                    : 'Blank assumes 3 months after the quotation date'
              }
              disabled={inputsLocked}
            />
            <NumField
              label="Discount (₱)"
              value={totals ? Math.round(totals.discount * 100) / 100 : 0}
              onChange={(v) => {
                if (!totals || totals.subtotal <= 0) return;
                setField('discountPct', discountPctFromAmount(totals.subtotal, v));
              }}
              helperText={
                totals && totals.subtotal > 0
                  ? 'Exact peso off the subtotal (e.g. negotiated cut)'
                  : 'Add line items first'
              }
              disabled={inputsLocked || !totals || totals.subtotal <= 0}
              sx={{ width: '100%' }}
            />
            <NumField
              label="Target net total (VAT-ex ₱)"
              value={totals ? Math.round((totals.subtotal - totals.discount) * 100) / 100 : 0}
              onChange={(v) => {
                if (!totals || totals.subtotal <= 0) return;
                setField('discountPct', discountPctFromTargetNet(totals.subtotal, v));
              }}
              helperText={
                totals && totals.subtotal > 0
                  ? 'Type the figure you want to charge (e.g. 532000 for 14 × 38000)'
                  : 'Add line items first'
              }
              disabled={inputsLocked || !totals || totals.subtotal <= 0}
              sx={{ width: '100%' }}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Section A */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} justifyContent="space-between" alignItems="center" mb={1}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>A. General Requirements</Typography>
            {generalReqtsLocked && !inputsLocked && (
              <Chip label="Locked — Supply only" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
            )}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={!!quotation.exportGeneralReqtsAsLot}
                  onChange={(e) => setField('exportGeneralReqtsAsLot', e.target.checked)}
                  disabled={generalReqtsLocked}
                />
              }
              label={<Typography variant="caption">Group in PDF/Excel</Typography>}
            />
            {!!quotation.exportGeneralReqtsAsLot && (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
                <NumField
                  label="Qty"
                  value={generalReqtsExportQty}
                  onChange={(v) => setField('generalReqtsExportQty', Math.max(1, v))}
                  integer
                  sx={{ width: 86 }}
                  disabled={generalReqtsLocked}
                />
                <Typography variant="caption" color="text.secondary">
                  Unit: {PHP(generalReqtsExportUnitPrice)} / LOT
                </Typography>
              </Stack>
            )}
          </Stack>
          {!generalReqtsLocked && <Button startIcon={<AddIcon />} size="small" onClick={addGeneral}>Add row</Button>}
        </Stack>
        <EditableTable
          rows={quotation.generalReqts}
          columns={generalCols}
          onChange={(idx, key, v) => updateRow('generalReqts', idx, key, v)}
          onDelete={(idx) => deleteRow('generalReqts', idx)}
          onReorder={(rows) => reorderRows('generalReqts', rows)}
          emptyMessage="No general requirements"
          readOnly={generalReqtsLocked}
          footer={
            <>
              {/* colSpan = columns.length: drag handle + all data cols except Total (amount sits under Total; trailing empty is delete) */}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={generalCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>Cost (no markup)</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(totals.generalReqtsCost)}</TableCell>
                <TableCell />
              </TableRow>
              {!!quotation.exportGeneralReqtsAsLot && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={generalCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                    Unit Price / LOT
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(generalReqtsExportUnitPrice)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={generalCols.length} align="right" sx={{ fontWeight: 600 }}>Subtotal (with markup)</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{PHP(totals.generalReqtsSubtotal)}</TableCell>
                <TableCell />
              </TableRow>
            </>
          }
        />
      </Paper>

      {/* Section B */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} justifyContent="space-between" alignItems="center" mb={1}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>B. Supply of Components</Typography>
            {componentsLocked && !inputsLocked && (
              <Chip label="Locked — Services only" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
            )}
            <FormControlLabel
              control={<Switch size="small" checked={!!quotation.hidePartNumbersInPdf} onChange={(e) => setField('hidePartNumbersInPdf', e.target.checked)} disabled={componentsLocked} />}
              label={<Typography variant="caption">Hide part numbers in PDF</Typography>}
            />
            <FormControlLabel
              control={<Switch size="small" checked={quotation.salesValueScope === 'services_only'} onChange={(e) => setField('salesValueScope', e.target.checked ? 'services_only' : undefined)} disabled={componentsLocked} />}
              label={<Typography variant="caption">Client supplies materials — Sales counts services only</Typography>}
            />
          </Stack>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {selectedCompIds.size >= 2 && !componentsLocked && (
              <Button size="small" variant="outlined" onClick={() => setGroupDialogOpen('components')}>
                Group selected ({selectedCompIds.size})
              </Button>
            )}
            {selectedCompIds.size >= 1 && !componentsLocked && (
              <Button size="small" variant="outlined" onClick={() => setSubheaderDialogOpen('components')}>
                Add subheader ({selectedCompIds.size})
              </Button>
            )}
            {selectedCompIds.size >= 1 && quotation.components.some((c) => selectedCompIds.has(c.id) && c.subheader) && !componentsLocked && (
              <Button size="small" variant="text" onClick={() => clearSelectedSubheaders('components')}>Clear subheader</Button>
            )}
            {!componentsLocked && (
              <>
                <Button startIcon={<MenuBookIcon />} size="small" variant="outlined" onClick={() => setCatalogOpen(true)}>
                  Browse Catalog
                </Button>
                <Button startIcon={<ConstructionIcon />} size="small" variant="outlined" onClick={() => setInstallWorkOpen(true)}>
                  Installation Work
                </Button>
                <Button startIcon={<MemoryIcon />} size="small" variant="outlined" onClick={() => setPlcOpen(true)}>
                  Siemens PLC
                </Button>
                <Button startIcon={<MemoryIcon />} size="small" variant="outlined" onClick={() => setDesigoOpen(true)}>
                  Desigo BMS
                </Button>
                <Button startIcon={<ConstructionIcon />} size="small" variant="outlined" onClick={() => setPanelOpen(true)}>
                  Control Panel
                </Button>
                <Button startIcon={<AddIcon />} size="small" onClick={addComponent}>Add row</Button>
              </>
            )}
          </Stack>
        </Stack>
        <EditableTable
          rows={quotation.components}
          columns={compCols}
          onChange={(idx, key, v) => updateRow('components', idx, key, v)}
          onDelete={(idx) => deleteRow('components', idx)}
          onReorder={(rows) => reorderRows('components', rows)}
          subheader={(row, index) => subheaderBefore(quotation.components, index)}
          emptyMessage="No components — typical for IOCT services-only quotes"
          readOnly={componentsLocked}
          isHeaderRow={(r) => !!r.isHeader}
          isChildHeaderRow={(r) => !!r.isChildHeader}
          getContextMenu={getComponentContextMenu}
          footer={
            <>
              {/* colSpan = columns.length so amount lands under Total (drag + data cols except Total) */}
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={compCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>Cost (no contingency/markup)</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(totals.componentsCost)}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={compCols.length} align="right" sx={{ fontWeight: 600 }}>Subtotal (with contingency + markup)</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{PHP(totals.componentsSubtotal)}</TableCell>
                <TableCell />
              </TableRow>
              {(totals.componentsOptionalSubtotal ?? 0) > 0 && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell colSpan={compCols.length} align="right" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Optional items (* not in contract total)</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace', fontStyle: 'italic', color: 'text.secondary' }}>{PHP(totals.componentsOptionalSubtotal ?? 0)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </>
          }
        />
      </Paper>

      {/* Section C — Scope of Works (deliverables) */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} justifyContent="space-between" alignItems="center" mb={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>C. Engineering Services</Typography>
              {servicesLocked && !inputsLocked && (
                <Chip label="Locked — Supply only" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {quotation.servicesFromManpower && quotation.servicesPerLinePricing
                ? 'Scope of Works — each deliverable priced individually. Manpower table below is the cost basis.'
                : quotation.servicesFromManpower
                  ? 'Scope of Works — deliverables shown as bullets in the quotation, priced as a lump sum below.'
                  : 'Scope of Works — each deliverable priced individually.'}
            </Typography>
          </Box>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
            <FormControlLabel
              control={<Switch size="small" checked={quotation.servicesFromManpower} onChange={(e) => setField('servicesFromManpower', e.target.checked)} disabled={servicesLocked} />}
              label={<Typography variant="caption">Price from manpower</Typography>}
            />
            {quotation.servicesFromManpower && (
              <>
                <FormControlLabel
                  control={<Switch size="small" checked={!!quotation.servicesPerLinePricing} onChange={(e) => setField('servicesPerLinePricing', e.target.checked)} disabled={servicesLocked} />}
                  label={<Typography variant="caption">Per-line pricing</Typography>}
                />
                {quotation.servicesPerLinePricing && (
                  <FormControlLabel
                    control={<Switch size="small" checked={!!quotation.servicesItemizedExport} onChange={(e) => setField('servicesItemizedExport', e.target.checked)} disabled={servicesLocked} />}
                    label={<Typography variant="caption">Show QTY/UOM in PDF &amp; Excel (else 1 LOT)</Typography>}
                  />
                )}
                {!quotation.servicesPerLinePricing && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <NumField
                      label="Qty"
                      value={engineeringServicesQty}
                      onChange={(v) => setField('engineeringServicesQty', Math.max(1, v))}
                      integer
                      sx={{ width: 86 }}
                      disabled={servicesLocked}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Unit: {PHP(engineeringServicesUnitPrice)} / LOT
                    </Typography>
                  </Stack>
                )}
              </>
            )}
            {perLinePricing && selectedSvcIds.size >= 2 && !servicesLocked && (
              <Button size="small" variant="outlined" onClick={() => setGroupDialogOpen('services')}>
                Group selected ({selectedSvcIds.size})
              </Button>
            )}
            {selectedSvcIds.size >= 1 && !servicesLocked && (
              <Button size="small" variant="outlined" onClick={() => setSubheaderDialogOpen('services')}>
                Add subheader ({selectedSvcIds.size})
              </Button>
            )}
            {selectedSvcIds.size >= 1 && quotation.services.some((s) => selectedSvcIds.has(s.id) && s.subheader) && !servicesLocked && (
              <Button size="small" variant="text" onClick={() => clearSelectedSubheaders('services')}>Clear subheader</Button>
            )}
            {!servicesLocked && <Button startIcon={<AddIcon />} size="small" onClick={addService}>Add scope item</Button>}
          </Stack>
        </Stack>
        <EditableTable
          rows={quotation.services}
          columns={
            quotation.servicesFromManpower && !quotation.servicesPerLinePricing
              ? svcCols.filter((c) => c.key !== 'amount')
              : svcCols
          }
          onChange={(idx, key, v) => updateServiceRow(idx, key as keyof ServiceLine, v)}
          onDelete={(idx) => deleteRow('services', idx)}
          onReorder={(rows) => reorderRows('services', rows)}
          subheader={(row, index) => subheaderBefore(quotation.services, index)}
          emptyMessage="No scope items — add deliverables (e.g., 'PLC redundancy troubleshooting', 'TIA Portal integration')"
          readOnly={servicesLocked}
          isHeaderRow={(r) => !!r.isHeader}
          isChildHeaderRow={(r) => !!r.isChildHeader}
          getContextMenu={getServiceContextMenu}
          footer={
            (!quotation.servicesFromManpower || quotation.servicesPerLinePricing) ? (
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={svcCols.length} align="right" sx={{ fontWeight: 600 }}>Services Subtotal</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{PHP(totals.servicesSubtotal)}</TableCell>
                <TableCell />
              </TableRow>
            ) : undefined
          }
        />

        {quotation.servicesFromManpower && (
          <Box sx={{ mt: 3 }}>
            <Stack direction="row" flexWrap="wrap" useFlexGap rowGap={1} columnGap={2} justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Manpower (cost basis)</Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={2} alignItems="center">
                {!quotation.servicesPerLinePricing && (
                  <Typography variant="caption" color="text.secondary">
                    Subtotal = Manpower cost / LOT × {engineeringServicesQty} LOT
                  </Typography>
                )}
                {!servicesLocked && <Button startIcon={<AddIcon />} size="small" onClick={addManpower}>Add manpower</Button>}
              </Stack>
            </Stack>
            <EditableTable
              rows={quotation.manpower}
              columns={mpCols}
              onChange={(idx, key, v) => updateRow('manpower', idx, key, v)}
              onDelete={(idx) => deleteRow('manpower', idx)}
              onReorder={(rows) => reorderRows('manpower', rows)}
              emptyMessage="No manpower entries — pick a role from the dropdown to auto-fill rate & allowance"
              readOnly={servicesLocked}
              footer={
                quotation.servicesPerLinePricing ? (
                  <>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>Team Daily Rate</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(teamDailyRate)}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 600 }}>Total Manpower Cost</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{PHP(totals.laborCost)}</TableCell>
                    <TableCell />
                  </TableRow>
                  </>
                ) : (
                <>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      {engineeringServicesQty > 1 ? 'Manpower Cost / LOT' : 'Manpower Cost'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {PHP(manpowerTotalCost(quotation.manpower))}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                  {engineeringServicesQty > 1 && (
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        Total Manpower Cost ({engineeringServicesQty} LOT)
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(totals.laborCost)}</TableCell>
                      <TableCell />
                    </TableRow>
                  )}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                      Unit Price / LOT
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{PHP(engineeringServicesUnitPrice)}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell colSpan={mpCols.length} align="right" sx={{ fontWeight: 600 }}>Services Subtotal (lump sum)</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{PHP(totals.servicesSubtotal)}</TableCell>
                    <TableCell />
                  </TableRow>
                </>
                )
              }
            />
          </Box>
        )}
      </Paper>

      {/* Group dialog */}
      <Dialog open={!!groupDialogOpen} onClose={() => setGroupDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Group {groupDialogOpen === 'components' ? 'components' : 'scope items'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {(groupDialogOpen === 'components' ? selectedCompIds.size : selectedSvcIds.size)} items selected. They will appear as a single "1 LOT" line in the PDF with the totalized amount.
          </Typography>
          <TextField label="Group label" value={groupLabel} onChange={(e) => setGroupLabel(e.target.value)} fullWidth autoFocus
            placeholder="e.g. Installation Works" onKeyDown={(e) => { if (e.key === 'Enter') applyGroup(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGroupDialogOpen(false); setGroupLabel(''); }}>Cancel</Button>
          <Button variant="contained" disabled={!groupLabel.trim()} onClick={applyGroup}>Group</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!subheaderDialogOpen} onClose={() => setSubheaderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add inline subheader</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This adds a visible category heading without changing quantities, pricing, or totals.
          </Typography>
          <TextField label="Subheader" value={subheaderLabel} onChange={(e) => setSubheaderLabel(e.target.value)} fullWidth autoFocus
            placeholder="e.g. PLC, SCADA, Computer Components" onKeyDown={(e) => { if (e.key === 'Enter') applySubheader(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSubheaderDialogOpen(false); setSubheaderLabel(''); }}>Cancel</Button>
          <Button variant="contained" disabled={!subheaderLabel.trim()} onClick={applySubheader}>Add subheader</Button>
        </DialogActions>
      </Dialog>

      {/* Totals */}
      <Paper sx={{ p: 2.5, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Totals</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', rowGap: 1, columnGap: 2, maxWidth: 560, ml: 'auto', mb: 2 }}>
          <Typography variant="body2">A. General Requirements</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.generalReqtsSubtotal)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 44 }}>{mixPct(totals.generalReqtsSubtotal)}</Typography>
          <Typography variant="body2">B. Supply of Components</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.componentsSubtotal)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 44 }}>{mixPct(totals.componentsSubtotal)}</Typography>
          <Typography variant="body2">C. Engineering Services</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.servicesSubtotal)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 44 }}>{mixPct(totals.servicesSubtotal)}</Typography>
          <Divider sx={{ gridColumn: 'span 3', my: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Subtotal (VAT-EX)</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', fontWeight: 600 }}>{PHP(totals.subtotal)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', minWidth: 44 }}>100%</Typography>
          {quotation.discountPct > 0 && <>
            <Typography variant="body2">Discount ({formatDiscountPct(quotation.discountPct)}%)</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right', color: 'error.main' }}>− {PHP(totals.discount)}</Typography>
            <Box />
          </>}
          {(totals.deliveryFee ?? 0) > 0 && <>
            <Typography variant="body2">Delivery Fee</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.deliveryFee ?? 0)}</Typography>
            <Box />
          </>}
          {(totals.smallOrderSurcharge ?? 0) > 0 && <>
            <Typography variant="body2">Small-order surcharge</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.smallOrderSurcharge ?? 0)}</Typography>
            <Box />
          </>}
          {!quotation.nonVat && quotation.vatPct > 0 && <>
            <Typography variant="body2">VAT ({quotation.vatPct}%)</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', textAlign: 'right' }}>{PHP(totals.vat)}</Typography>
            <Box />
          </>}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', maxWidth: 560, ml: 'auto', mb: 2, mt: -1 }}>
          % = share of Subtotal (VAT-EX) — use to sanity-check the cost mix while balancing the sheet
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'success.light', borderRadius: 1, bgcolor: 'rgba(46,125,50,0.05)', textAlign: 'right', minWidth: { xs: 0, sm: 240 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Total Margin (markup only)</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'success.dark', fontWeight: 700 }}>
              {PHP(marginSummary?.value ?? 0)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'success.dark', fontWeight: 600 }}>
              {(marginSummary?.pct ?? 0).toFixed(1)}% margin
            </Typography>
            {marginSummary && marginSummary.contingency > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                + Contingency reserve: {PHP(marginSummary.contingency)} ({marginSummary.contingencyPct.toFixed(1)}%)
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 2, bgcolor: 'primary.main', borderRadius: 1, textAlign: 'right', minWidth: { xs: 0, sm: 220 } }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', display: 'block', mb: 0.5 }}>GRAND TOTAL</Typography>
            <Typography variant="h5" sx={{ fontFamily: 'monospace', color: 'white', fontWeight: 700 }}>
              {PHP(totals.grandTotal)}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Cost breakdown — internal only, never appears in PDF */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Internal Breakdown (not shown to client)</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <BreakdownCard label="A. General Req." color="primary"
            cost={totals.generalReqtsCost}
            contingency={null}
            contingencyPct={null}
            markup={totals.generalReqtsSubtotal - totals.generalReqtsWithContingency}
            markupPct={quotation.generalReqMarkupPct}
            subtotal={totals.generalReqtsSubtotal}
          />
          <BreakdownCard label="B. Components" color="secondary"
            cost={totals.componentsCost}
            contingency={(totals.componentsWithContingency ?? totals.componentsCost) - totals.componentsCost}
            contingencyPct={quotation.productContingencyPct ?? 0}
            markup={totals.componentsSubtotal - (totals.componentsWithContingency ?? totals.componentsCost)}
            markupPct={quotation.productMarkupPct}
            subtotal={totals.componentsSubtotal}
          />
          <BreakdownCard label="C. Labor" color="primary"
            cost={totals.laborCost}
            contingency={quotation.servicesFromManpower ? totals.laborWithContingency - totals.laborCost : null}
            contingencyPct={quotation.servicesFromManpower ? quotation.globalContingencyPct : null}
            // Manual lump-sum lines have no markup step of their own (the
            // typed amount is the final price) — but EWT still rides in at
            // display time (serviceLineAmount), so the delta here is EWT-only.
            // Derive it the same way as the per-line-pricing branch below
            // rather than hardcoding 0, so cost + markup always sums to the
            // printed subtotal.
            markup={quotation.servicesFromManpower ? totals.servicesSubtotal - totals.laborWithContingency : totals.servicesSubtotal - totals.laborCost}
            markupPct={quotation.servicesFromManpower
              ? (quotation.servicesPerLinePricing
                ? (totals.laborCost > 0 ? ((totals.servicesSubtotal - totals.laborCost) / totals.laborCost) * 100 : 0)
                : quotation.laborMarkupPct)
              : (totals.laborCost > 0 ? ((totals.servicesSubtotal - totals.laborCost) / totals.laborCost) * 100 : 0)}
            subtotal={totals.servicesSubtotal}
          />
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">Total Margin (markup only)</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace', color: 'success.main', mt: 0.5, fontWeight: 700 }}>
              {PHP(marginSummary?.value ?? 0)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 600 }}>
              {(marginSummary?.pct ?? 0).toFixed(1)}% margin
            </Typography>
            {marginSummary && marginSummary.contingency > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Contingency reserve: {PHP(marginSummary.contingency)} ({marginSummary.contingencyPct.toFixed(1)}%)
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Phones: keep the numbers that matter in view while editing lines */}
      <Box
        sx={{
          display: 'flex', position: 'sticky', bottom: 8, zIndex: 5,
          alignItems: 'center', justifyContent: 'space-between', gap: 1,
          px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'primary.main', color: 'white', boxShadow: 6,
          // Shown on every phone/tablet screen (portrait or landscape); hidden on laptops/desktops.
          '@media (min-width: 900px) and (min-height: 601px)': { display: 'none' },
          // Landscape phones are short — keep it slim.
          '@media (max-height: 600px)': { py: 0.5 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)', lineHeight: 1.2 }}>Margin</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
            {(marginSummary?.pct ?? 0).toFixed(1)}%
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.75)', lineHeight: 1.2 }}>GRAND TOTAL</Typography>
          <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', fontWeight: 700, lineHeight: 1.2 }}>{PHP(totals.grandTotal)}</Typography>
        </Box>
      </Box>

      {/* Saved-version history */}
      <DuplicateQuotationDialog
        open={duplicateOpen}
        quotation={quotation}
        onClose={() => setDuplicateOpen(false)}
        onDuplicated={(copy) => {
          setDuplicateOpen(false);
          navigate(`/sales/calcsheet/quotations/${copy.id}`);
        }}
      />

      <CopyInclusionsDialog
        open={copyOpen}
        currentQuotationId={quotation.id}
        onClose={() => setCopyOpen(false)}
        onCopy={handleCopyInclusions}
      />

      <ScopeLibraryDialog
        open={libraryOpen}
        current={{
          generalReqts: draft?.generalReqts ?? [],
          components: draft?.components ?? [],
          services: draft?.services ?? [],
          manpower: draft?.manpower ?? [],
          terms: (draft?.termsOverrides?.scopeOfWork || draft?.termsOverrides?.exclusions)
            ? { scopeOfWork: draft?.termsOverrides?.scopeOfWork, exclusions: draft?.termsOverrides?.exclusions }
            : undefined,
        }}
        onClose={() => setLibraryOpen(false)}
        onInsert={handleInsertFromLibrary}
        onSaved={(name) => setToast({ msg: `Saved “${name}” to the Scope Library.`, sev: 'success' })}
      />

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Version history — {quotationRefNo(project.code, recipient?.code, quotation.revision)} ({quotation.kind})
          <Typography variant="body2" color="text.secondary">
            A snapshot of the previous state is kept every time this quotation is saved.
            The current state is what you see in the editor.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack>
          ) : historyError ? (
            <Alert severity="error">Could not load history: {historyError}</Alert>
          ) : versions.length === 0 ? (
            <Alert severity="info">
              No earlier versions yet. History starts recording from the next time this quotation is saved.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Replaced on</TableCell>
                  <TableCell>Saved by</TableCell>
                  <TableCell align="right">Grand Total</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((v, i) => {
                  let total = '—';
                  try { total = PHP(computeTotals(v.data).grandTotal); } catch { /* malformed snapshot */ }
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{versions.length - i}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {v.savedAt ? format(new Date(v.savedAt), 'yyyy-MM-dd HH:mm') : '—'}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{v.savedBy || '—'}</Typography>
                          {v.remarks && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                              "{v.remarks}"
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{total}</TableCell>
                      <TableCell align="right">
                        {!inputsLocked && (
                          <Button size="small" startIcon={<UndoIcon />} onClick={() => restoreVersion(v)}>
                            Restore
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          title="Delete this snapshot"
                          disabled={deletingVersionId === v.id}
                          onClick={() => handleDeleteVersion(v)}
                        >
                          {deletingVersionId === v.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={remarkDialogOpen}
        onClose={() => setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        // Enter submits like a commit-message prompt; Shift+Enter adds a line.
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (saveTermsConfirmed) void confirmSave(remarkText);
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>What did you change?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional — shown in Version history next to this save. Leave blank to save without a note.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            placeholder="e.g. Added optional spare CPU, adjusted Panel Assembly pricing"
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Current Delivery terms on this quotation:
          </Typography>
          <Box sx={{ p: 1, mb: 1.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-line' }}>
            <Typography variant="caption" color="text.secondary">{effectiveDeliveryText}</Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={saveTermsConfirmed}
                onChange={(e) => setSaveTermsConfirmed(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I've reviewed and confirmed the Terms &amp; Conditions — including Delivery — are correct for this quotation.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRemarkDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!saveTermsConfirmed}
            onClick={() => confirmSave(remarkText)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={exportConfirmOpen}
        onClose={() => setExportConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (exportTermsConfirmed) confirmExportPdf();
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Terms &amp; Conditions</DialogTitle>
        <DialogContent>
          {exportConfirmOpen && <InternalTextWarning findings={findInternalLookingText(quotation)} />}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Before exporting this quotation to the client, please confirm the Terms &amp; Conditions
            — especially Delivery — are correct and up to date.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Current Delivery terms on this quotation:
          </Typography>
          <Box sx={{ p: 1, mb: 1.5, bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-line' }}>
            <Typography variant="caption" color="text.secondary">{effectiveDeliveryText}</Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                autoFocus
                checked={exportTermsConfirmed}
                onChange={(e) => setExportTermsConfirmed(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                I've reviewed and confirmed the Terms &amp; Conditions — including Delivery — are correct for this quotation.
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
            disabled={!exportTermsConfirmed}
            onClick={confirmExportPdf}
          >
            Confirm &amp; Export
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={xlsxWarnOpen} onClose={() => setXlsxWarnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Possible internal text</DialogTitle>
        <DialogContent>
          {xlsxWarnOpen && <InternalTextWarning findings={findInternalLookingText(quotation)} />}
          <Typography variant="body2" color="text.secondary">Export the Excel anyway, or go back and move that wording into the lines' internal notes?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setXlsxWarnOpen(false)}>Go back</Button>
          <Button variant="contained" startIcon={<GridOnIcon />} onClick={() => { setXlsxWarnOpen(false); void exportXlsx(); }}>Export anyway</Button>
        </DialogActions>
      </Dialog>

      <InternalNoteDialog
        open={!!noteTarget}
        lineLabel={noteLine ? (noteLine.description || noteLine.code || '') : ''}
        note={noteLine?.internalNote}
        readOnly={inputsLocked}
        onSave={saveInternalNote}
        onClose={() => setNoteTarget(null)}
      />

      <ProductPickerDialog
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onAddPricelist={addFromCatalog}
        onAddHistory={addFromHistory}
        analysisDate={quotationDate}
        expectedPurchaseDate={effectiveExpectedPurchaseDate}
        defaultContingencyPct={quotation.productContingencyPct ?? 0}
      />

      <SiemensPlcDialog
        open={plcOpen}
        onClose={() => setPlcOpen(false)}
        productContingencyPct={quotation.productContingencyPct ?? 0}
        onSubmit={addFromPlc}
      />
      <DesigoBmsDialog
        open={desigoOpen}
        onClose={() => setDesigoOpen(false)}
        productContingencyPct={quotation.productContingencyPct ?? 0}
        onSubmit={addFromPlc}
      />
      <ControlPanelDialog
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        productContingencyPct={quotation.productContingencyPct ?? 0}
        onSubmit={addFromPlc}
      />
      <InstallationWorkDialog
        open={installWorkOpen}
        onClose={() => setInstallWorkOpen(false)}
        productContingencyPct={quotation.productContingencyPct ?? 0}
        onSubmit={addFromInstallationWork}
      />

      <ComponentTimingDialog
        open={Boolean(timingComponent)}
        value={timingComponent?.expectedPurchaseDate}
        quotationExpectedPurchaseDate={quotation.expectedPurchaseDate}
        minimumDate={quotationDate}
        onClose={() => setTimingComponentId(null)}
        onSave={(value) => {
          if (!timingComponent) return;
          setField('components', quotation.components.map((line) => (
            line.id === timingComponent.id
              ? { ...line, expectedPurchaseDate: value || undefined }
              : line
          )));
          setTimingComponentId(null);
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.sev === 'success' ? 3500 : 6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.sev} variant="filled" sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>

      {showFloatingTotals && (
        <FloatingTotalsWidget
          totals={totals}
          marginSummary={marginSummary}
          quotation={quotation}
          onClose={toggleFloatingTotals}
        />
      )}
    </Stack>
  );
}
